import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, insertRow, updateRow, subirRegistroVehicular } from '../../db/engine';
import { getConfig, generarGestionesContables } from '../../db/contabilidad';
import { sincronizarAsignaciones } from '../../db/crmAsignacion';
import { decisionesNoRenueva, decidioNoRenovar as decidioNoRenovarEn, MARCA_NO_RENUEVA } from '../../db/renovaciones';
import { generarPeriodosPendientes, generarRecordatoriosRegularizacion } from '../../db/periodos';
import { fetchNotifConfig, fetchPlantillas, rellenar, enviarNotificacion } from '../../db/notificaciones';
import { reaplicarSacoPoliza } from '../cobros/Cobros';
import { authHeaders } from '../../utils/apiIA';
import { useApp } from '../../context/AppContext';
import { abrirDocumento } from '../../utils/archivos';
import { fmtMoneda } from '../../utils/moneda';
import PolizaVistaModal from '../../components/PolizaVistaModal';
import PolizaSelect from '../../components/PolizaSelect';
import ClienteSelect from '../../components/ClienteSelect';
import { Plus, X, ClipboardList, CheckCircle, AlertCircle, UserCheck, Clock, CreditCard, RefreshCw, Mail } from 'lucide-react';
import { fmtFecha, fmtFechaHora } from '../../utils/fecha';

import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { getAbrev } from '../../utils/marca';

const TIPOS = ['cobro', 'renovacion', 'endoso', 'declaracion', 'cotizacion', 'reclamo', 'seguimiento', 'obligacion', 'otro'];
// Ciclo de una solicitud de endoso (para mostrar el paso actual en la tarea del CRM)
const ENDOSO_PASOS = [
  { key:'enviada_aseg',    label:'Enviada a aseguradora' },
  { key:'endoso_recibido', label:'Endoso recibido' },
  { key:'enviado_cliente', label:'Enviado al cliente' },
];
const ENDOSO_ORDEN = ['borrador', 'enviada_aseg', 'endoso_recibido', 'enviado_cliente'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];
const PRIORIDAD_COLOR = { baja:'badge-neutral', normal:'badge-info', alta:'badge-warning', urgente:'badge-danger' };
const EMPTY = {
  tipo:'seguimiento', titulo:'', descripcion:'', cliente_id:'', poliza_id:'',
  agente_id:'', prioridad:'normal', estado:'abierto', fecha_vencimiento:'',
  asignado_a:'', motivo_asignacion:'',
};

export default function CRM() {
  const { user, canEdit, esAgenteRestringido, esEjecutivo } = useApp();
  const abrev = getAbrev();
  const navigate = useNavigate();
  const [rows, setRows]         = useState([]);
  const [filterPoliza, setFilterPoliza] = useState('');
  const [filterTipo, setFilterTipo]     = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [filterEstado, setFilterEstado] = useState('dia'); // 'dia' = tareas para gestionar hoy
  const [filterAsignado, setFilterAsignado] = useState('mis'); // 'mis' | 'todos'
  const [sortCol, setSortCol]   = useState('');   // columna de orden
  const [sortDir, setSortDir]   = useState('asc'); // 'asc' | 'desc'
  const [modal, setModal]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [endosoSolEstado, setEndosoSolEstado] = useState(null); // estado del ciclo de la solicitud de endoso ligada a la tarea
  const [clienteContacto, setClienteContacto] = useState(null); // datos de contacto del cliente (teléfonos/emails) para la vista
  const [avanzandoEndoso, setAvanzandoEndoso] = useState(false); // guardando avance de ciclo del endoso
  const [endosoDocFile, setEndosoDocFile]     = useState(null);  // documento del endoso recibido a adjuntar
  const [subiendoEndosoDoc, setSubiendoEndosoDoc] = useState(false);
  const [endosoDocUrl, setEndosoDocUrl]       = useState(null);  // url del endoso recibido ya adjunto (si existe)
  const [emailClienteFile, setEmailClienteFile] = useState(null);// correo enviado al cliente a adjuntar
  const [analizandoIA, setAnalizandoIA]       = useState(false); // corriendo validación con IA
  const [iaResultado, setIaResultado]         = useState(null);  // { coincide, resumen, discrepancias } | { error }
  const [confirmoEndoso, setConfirmoEndoso]   = useState(false); // confirmación manual (si no se usa la IA)
  const [endosoMovioPrima, setEndosoMovioPrima] = useState(false); // el endoso mueve prima (para la lógica de corrección)
  const [reversando, setReversando]           = useState(false); // reversando el ciclo (superusuario)
  const [emailModal, setEmailModal]           = useState(false); // modal "enviar al cliente" (PDF o pegar texto)
  const [emailModalMode, setEmailModalMode]   = useState('file'); // 'file' | 'texto'
  const [emailTexto, setEmailTexto]           = useState('');
  const [enviandoCliente, setEnviandoCliente] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [clientes, setClientes] = useState([]);
  const [polizas, setPolizas]   = useState([]);
  const [agentes, setAgentes]   = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [bitacora, setBitacora] = useState([]);       // bitacora_cobros o bitacora_polizas según tipo
  const [polAgentesNombres, setPolAgentesNombres] = useState(''); // agente(s) de la póliza de la tarea
  const [asignandoA, setAsignandoA]   = useState('');
  const [motivoAsig, setMotivoAsig]   = useState('');
  const [showAsignar, setShowAsignar] = useState(false);
  const [pagosPoliza, setPagosPoliza] = useState([]);
  const [endososTarea, setEndososTarea] = useState([]); // endosos de la póliza de la tarea (pestaña Endosos)
  const [showPosponer, setShowPosponer] = useState(false);
  const [fechaPosponer, setFechaPosponer] = useState('');
  const [tabVista, setTabVista] = useState('info'); // 'info' | 'pagos'

  // Póliza info modal
  // Ver la póliza de la tarea como capa (la MISMA vista del módulo Pólizas). Al
  // cerrarla se vuelve al modal de la tarea, que queda abierto por debajo.
  const [verPolizaId, setVerPolizaId] = useState(null);

  // Cierre contextual
  const [closingType, setClosingType] = useState(null); // 'poliza_cancelada'|'no_renueva'|'completada'
  const [closeNota, setCloseNota]     = useState('');

  // Resolver tarea de placa (registrar placa real + adjuntar RUV)
  const [placaNum, setPlacaNum]               = useState('');
  const [placaFile, setPlacaFile]             = useState(null);
  const [resolviendoPlaca, setResolviendoPlaca] = useState(false);

  // Notificaciones por correo (cobro / renovación)
  const [notifCfg, setNotifCfg]               = useState({ habilitado:false });
  const [notifPlantillas, setNotifPlantillas] = useState({});
  const [polizaDetalle, setPolizaDetalle]     = useState(null); // datos de la póliza de la tarea (para la plantilla)
  const [correoPreview, setCorreoPreview]     = useState(null); // { tipo, destinatario, asunto, cuerpo }
  const [cargandoCorreo, setCargandoCorreo]   = useState(false);
  const [enviandoCorreo, setEnviandoCorreo]   = useState(false);

  const load = useCallback(async () => {
    let q = supabase.from('crm_gestiones')
      .select('*, clientes(nombre), polizas(numero, ejecutivos!polizas_ejecutivo_id_fkey(nombre), poliza_agentes(agentes(nombre))), agentes(nombre), asignado_usuario:usuarios!crm_gestiones_asignado_a_fkey(nombre), asignador:usuarios!crm_gestiones_asignado_por_fkey(nombre)')
      .order('prioridad').order('fecha_vencimiento', { ascending:true });
    if (filterTipo) q = q.eq('tipo', filterTipo);
    if (filterCliente) q = q.eq('cliente_id', filterCliente);
    if (filterPoliza) q = q.eq('poliza_id', filterPoliza);

    if (filterEstado === 'pospuesta') {
      // Solo pospuestas activas (posponer_hasta > hoy)
      q = q.gt('posponer_hasta', new Date().toISOString().slice(0,10))
           .in('estado', ['abierto','en_proceso']);
    } else if (filterEstado === 'dia') {
      // "Del día": abiertas/en proceso; el recorte por vencimiento y el ocultar
      // pospuestas se hace abajo en memoria (para incluir tareas sin fecha).
      q = q.in('estado', ['abierto','en_proceso']);
    } else if (filterEstado) {
      q = q.eq('estado', filterEstado);
    } else {
      // "Todas (abiertas)" = todas las tareas activas (no cerradas).
      // Las cerradas no se listan: su seguimiento queda en la bitácora de la póliza.
      q = q.neq('estado', 'cerrado');
    }

    // Filtrar por asignado
    if (esAgenteRestringido && user?.id) {
      // Agente restringido: solo SUS tareas (no ve las de la operación)
      q = q.eq('asignado_a', user.id);
    } else if (filterAsignado === 'mis' && user?.id) {
      // "Mis tareas": el ejecutivo ve SOLO las tareas de sus pólizas (asignadas a él),
      // sin las tareas sin asignar. La operación (no ejecutivo) ve las suyas + las sin asignar.
      if (esEjecutivo) q = q.eq('asignado_a', user.id);
      else             q = q.or(`asignado_a.is.null,asignado_a.eq.${user.id}`);
    }
    // "Todos" (filterAsignado === 'todos'): sin filtro de asignado — el ejecutivo
    // conserva la opción de ver el tablero completo.
    const { data } = await q;
    let arr = (data || []).map(g => ({
      ...g,
      cliente_nombre: g.clientes?.nombre,
      poliza_num: g.polizas?.numero,
      poliza_ejecutivo: g.polizas?.ejecutivos?.nombre || null,
      poliza_agentes: (g.polizas?.poliza_agentes || []).map(a => a.agentes?.nombre).filter(Boolean).join(', '),
      agente_nombre: g.agentes?.nombre,
      asignado_nombre: g.asignado_usuario?.nombre,
      asignador_nombre: g.asignador?.nombre,
    }));
    const hoyStr = new Date().toISOString().slice(0,10);
    // Pospuestas al futuro: no salen en ninguna vista salvo el filtro "Pospuestas".
    // Reaparecen solas cuando llega su fecha (posponer_hasta <= hoy).
    if (filterEstado !== 'pospuesta') {
      arr = arr.filter(g => !(g.posponer_hasta && g.posponer_hasta > hoyStr));
    }
    // "Del día": solo lo que toca gestionar hoy = vencidas o que vencen hoy (o sin fecha).
    if (filterEstado === 'dia') {
      arr = arr.filter(g => !g.fecha_vencimiento || g.fecha_vencimiento <= hoyStr);
    }
    setRows(arr);
  }, [filterPoliza, filterTipo, filterCliente, filterEstado, filterAsignado, user?.id, esAgenteRestringido, esEjecutivo]);

  useEffect(() => { load(); }, [load]);
  // Auto-generar tareas al entrar al CRM, pero SOLO si no se corrió hace poco.
  // La auditoría es pesada (recorre toda la operación); con el freno, entrar y salir
  // del CRM ya no la re-dispara cada vez. El botón "Actualizar" la fuerza a mano.
  useEffect(() => {
    const GEN_THROTTLE_MS = 10 * 60 * 1000; // 10 min
    const last = Number(localStorage.getItem('crm_last_gen') || 0);
    if (Date.now() - last > GEN_THROTTLE_MS) generarTareasAutomaticas();
  }, []); // eslint-disable-line
  // Volver a abrir una tarea si venimos de registrar el endoso en Solicitudes
  useEffect(() => {
    const taskId = sessionStorage.getItem('openCrmTaskId');
    if (!taskId) return;
    sessionStorage.removeItem('openCrmTaskId');
    supabase.from('crm_gestiones')
      .select('*, clientes(nombre), polizas(numero, ejecutivos!polizas_ejecutivo_id_fkey(nombre), poliza_agentes(agentes(nombre))), agentes(nombre), asignado_usuario:usuarios!crm_gestiones_asignado_a_fkey(nombre), asignador:usuarios!crm_gestiones_asignado_por_fkey(nombre)')
      .eq('id', taskId).maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        openView({
          ...data,
          cliente_nombre: data.clientes?.nombre,
          poliza_num: data.polizas?.numero,
          poliza_ejecutivo: data.polizas?.ejecutivos?.nombre || null,
          poliza_agentes: (data.polizas?.poliza_agentes || []).map(a => a.agentes?.nombre).filter(Boolean).join(', '),
          agente_nombre: data.agentes?.nombre,
          asignado_nombre: data.asignado_usuario?.nombre,
          asignador_nombre: data.asignador?.nombre,
        });
      });
  }, []); // eslint-disable-line
  // Config + plantillas de notificaciones (para gatear el envío de correo)
  useEffect(() => { (async () => {
    setNotifCfg(await fetchNotifConfig());
    setNotifPlantillas(await fetchPlantillas());
  })(); }, []);

  const openCreate = async () => {
    const [c, a, p, u] = await Promise.all([
      supabase.from('clientes').select('id, nombre').order('nombre'),
      supabase.from('agentes').select('id, nombre').eq('activo', true).order('nombre'),
      supabase.from('polizas').select('id, numero, clientes(nombre)').eq('estado','activa').order('numero'),
      supabase.from('usuarios').select('id, nombre').eq('activo', true).order('nombre'),
    ]);
    setClientes(c.data || []);
    setAgentes(a.data || []);
    setPolizas((p.data || []).map(x => ({ id:x.id, numero:x.numero, cliente:x.clientes?.nombre })));
    setUsuarios(u.data || []);
    setForm(EMPTY); setModal('create');
  };


  const handleCerrarConMotivo = async (motivo, notaTexto) => {
    if (!notaTexto?.trim()) return alert('Debes ingresar una nota que justifique el cierre.');
    try {
      await updateRow('crm_gestiones', selected.id, { estado:'cerrado', actualizado_en: new Date().toISOString() });
      await insertRow('crm_historial', {
        gestion_id: selected.id, usuario_id: user?.id,
        accion: 'cerrar',
        detalle: `[${motivo}] ${notaTexto.trim()}`,
      });
      if (selected.poliza_id) {
        const tabla = selected.tipo === 'cobro' ? 'bitacora_cobros' : 'bitacora_polizas';
        await insertRow(tabla, {
          poliza_id: selected.poliza_id, usuario_id: user?.id,
          nota: `Tarea cerrada — ${motivo}: ${notaTexto.trim()}`,
          ...(selected.tipo !== 'cobro' && { tipo: selected.tipo }),
        });
      }
      // "Cliente no renueva" sobre una póliza YA vencida → estado 'no_renovada' en el acto
      // (vencida = venció y nadie la gestionó; no_renovada = se gestionó y se decidió).
      // Si aún no vence, sigue 'activa' y la pasa a no_renovada la auto-actualización de
      // Pólizas el día que venza. Solo para ESTA vigencia (mismo vencimiento que la tarea).
      // Si el rol no puede editar pólizas, no frena el cierre: la auto-actualización lo hará.
      if (`[${motivo}]` === MARCA_NO_RENUEVA && selected.tipo === 'renovacion' && selected.poliza_id) {
        try {
          const { data: pol } = await supabase.from('polizas')
            .select('estado, fecha_vencimiento').eq('id', selected.poliza_id).maybeSingle();
          const mismaVig = pol?.fecha_vencimiento && selected.fecha_vencimiento &&
            String(pol.fecha_vencimiento).slice(0, 10) === String(selected.fecha_vencimiento).slice(0, 10);
          if (pol?.estado === 'vencida' && mismaVig) {
            await updateRow('polizas', selected.poliza_id, { estado: 'no_renovada' });
          }
        } catch { /* lo corrige la auto-actualización de estados de Pólizas */ }
      }
      setClosingType(null); setCloseNota('');
      await load(); setModal(null);
    } catch(e) { alert('Error: ' + e.message); }
  };

  // Una tarea de placa es la que se autogenera al crear/editar una póliza de auto
  // con la placa "Por reportar" (título "Actualizar placa — Póliza ...").
  const esTareaPlaca = (t) => !!t && /actualizar placa/i.test(t.titulo || '');
  // Tarea de endoso ligada a una solicitud: NO se cierra a mano. Se cierra sola cuando el
  // endoso se factura (solicitud convertida) o cuando se elimina/cancela la solicitud (con motivo).
  const esTareaEndosoBloqueada = (t) => !!t && t.tipo === 'endoso' && !!t.solicitud_id;

  // Resolver la tarea de placa: registra la placa real en el auto de la póliza,
  // sube el registro vehicular (RUV) a los documentos de la póliza y cierra la tarea.
  // Es el cierre BLINDADO: sin placa + RUV no se puede cerrar.
  const resolverPlaca = async () => {
    if (!placaNum.trim()) return alert('Ingresa el número de placa.');
    if (!placaFile)       return alert('Adjunta el registro vehicular (RUV).');
    if (!selected?.poliza_id) return alert('Esta tarea no tiene póliza vinculada.');
    setResolviendoPlaca(true);
    try {
      const placaVal = placaNum.trim().toUpperCase();
      // 1) Subir el RUV y registrarlo en los documentos de la póliza.
      await subirRegistroVehicular(selected.poliza_id, selected.cliente_id || null, placaFile);
      // 2) Escribir la placa real en el auto de la póliza (queda ligada a la póliza).
      const { data: autoRow } = await supabase.from('poliza_autos')
        .select('id').eq('poliza_id', selected.poliza_id).limit(1).maybeSingle();
      if (autoRow?.id) {
        await updateRow('poliza_autos', autoRow.id, { placa: placaVal, placa_por_reportar: false });
      } else {
        await insertRow('poliza_autos', { poliza_id: selected.poliza_id, placa: placaVal, placa_por_reportar: false });
      }
      // 3) Cerrar la tarea + dejar rastro en el log y la bitácora de la póliza.
      await updateRow('crm_gestiones', selected.id, { estado:'cerrado', actualizado_en: new Date().toISOString() });
      const detalle = `Placa registrada: ${placaVal}. Registro vehicular (RUV) adjunto a los documentos de la póliza.`;
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion:'cerrar', detalle });
      await insertRow('bitacora_polizas', { poliza_id: selected.poliza_id, usuario_id: user?.id, nota: detalle, tipo:'seguimiento' });
      setPlacaNum(''); setPlacaFile(null);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || e)); }
    finally { setResolviendoPlaca(false); }
  };

  // ── Notificación por correo (cobro / renovación) ──────────────
  const clienteCorreo = polizaDetalle?.clientes?.email || '';

  const abrirPreviewCorreo = async () => {
    if (!selected) return;
    if (!clienteCorreo) { alert('El cliente no tiene correo registrado.'); return; }
    setCargandoCorreo(true);
    try {
      const saldo = pagosPoliza
        .filter(p => p.estado !== 'pagado')
        .reduce((s, p) => s + Math.max(0, (parseFloat(p.monto)||0) - (parseFloat(p.monto_pagado)||0)), 0);
      const datos = {
        cliente:           polizaDetalle?.clientes?.nombre || selected.cliente_nombre || '',
        primer_nombre:     polizaDetalle?.clientes?.primer_nombre || '',
        numero_poliza:     polizaDetalle?.numero || selected.poliza_num || '',
        aseguradora:       polizaDetalle?.aseguradoras?.nombre || '',
        saldo:             fmt$(saldo),
        fecha_vencimiento: fmtFecha(polizaDetalle?.fecha_vencimiento) || '',
        fecha_inicio:      fmtFecha(polizaDetalle?.fecha_inicio) || '',
        correo:            clienteCorreo,
      };
      const pl = notifPlantillas[selected.tipo] || {};
      setCorreoPreview({
        tipo: selected.tipo,
        destinatario: clienteCorreo,
        asunto: rellenar(pl.asunto, datos),
        cuerpo: rellenar(pl.cuerpo, datos),
      });
    } finally { setCargandoCorreo(false); }
  };

  const enviarCorreo = async () => {
    if (!correoPreview || !selected) return;
    setEnviandoCorreo(true);
    try {
      const res = await enviarNotificacion({
        tipo: correoPreview.tipo,
        asunto: correoPreview.asunto,
        cuerpo: correoPreview.cuerpo,
        datos: {},
        cliente_id: selected.cliente_id || null,
        poliza_id: selected.poliza_id || null,
        destinatario: correoPreview.destinatario,
        usuario_id: user?.id,
        remitente: { nombre: notifCfg.remitente_nombre, correo: notifCfg.remitente_correo },
      });
      const etiqueta = res.estado === 'enviado' ? 'Correo enviado'
        : res.estado === 'no_configurado' ? 'Correo preparado (envío aún no activo)'
        : 'Correo registrado';
      const nota = `${etiqueta} — ${correoPreview.tipo === 'cobro' ? 'cobro' : 'renovación'} a ${correoPreview.destinatario}: "${correoPreview.asunto}"`;
      if (selected.poliza_id) {
        const tabla = selected.tipo === 'cobro' ? 'bitacora_cobros' : 'bitacora_polizas';
        const payload = { poliza_id: selected.poliza_id, usuario_id: user?.id, nota };
        if (selected.tipo !== 'cobro') payload.tipo = selected.tipo;
        await insertRow(tabla, payload);
        const { data: bitRows } = await supabase.from(tabla).select('*, usuarios(nombre)').eq('poliza_id', selected.poliza_id).order('creado_en', { ascending:false });
        setBitacora((bitRows || []).map(b => ({ ...b, usuario_nombre: b.usuarios?.nombre || null })));
      }
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'correo', detalle: nota });
      const { data: histRows } = await supabase.from('crm_historial').select('*, usuarios(nombre)').eq('gestion_id', selected.id).order('creado_en', { ascending:false });
      setHistorial((histRows || []).map(h => ({ ...h, usuario: h.usuarios?.nombre })));
      setCorreoPreview(null);
      // La gestión = enviar + posponer: abrir el panel de posponer para fijar la fecha.
      setShowPosponer(true); setShowAsignar(false);
      if (res.estado === 'enviado')            alert('Correo enviado al cliente. Ahora indica hasta cuándo pospones la tarea.');
      else if (res.estado === 'no_configurado') alert('Gestión registrada. El envío real se activará cuando se configure el correo. Ahora indica hasta cuándo pospones la tarea.');
      else                                      alert(`Gestión registrada, pero el correo NO salió: ${res.detalle || 'motivo desconocido'}. Indica hasta cuándo pospones la tarea.`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setEnviandoCorreo(false); }
  };

  const openView = async (r) => {
    setSelected(r);
    setShowAsignar(false); setAsignandoA(''); setMotivoAsig('');
    setShowPosponer(false); setFechaPosponer(''); setTabVista('info');
    setClosingType(null); setCloseNota('');
    // Tarea de endoso: traer el estado del ciclo de su solicitud + documentos ya adjuntos.
    setEndosoSolEstado(null); setEndosoDocFile(null); setEmailClienteFile(null);
    setEndosoDocUrl(null); setIaResultado(null);
    setConfirmoEndoso(false); setEndosoMovioPrima(false);
    if (r.tipo === 'endoso' && r.solicitud_id) {
      supabase.from('solicitudes').select('estado, prima').eq('id', r.solicitud_id).maybeSingle()
        .then(({ data }) => { setEndosoSolEstado(data?.estado || null); setEndosoMovioPrima((parseFloat(data?.prima) || 0) !== 0); });
      supabase.from('solicitud_archivos').select('url, tipo_doc').eq('solicitud_id', r.solicitud_id)
        .then(({ data }) => {
          const arr = data || [];
          setEndosoDocUrl(arr.find(a => a.tipo_doc === 'Endoso recibido')?.url || null);
        });
    }
    // Contactos del cliente (teléfonos / emails) para mostrarlos en la vista.
    setClienteContacto(null);
    if (r.cliente_id) {
      supabase.from('clientes').select('*').eq('id', r.cliente_id).maybeSingle()
        .then(({ data }) => setClienteContacto(data || null));
    }
    setPlacaNum(''); setPlacaFile(null); setResolviendoPlaca(false);
    setPolizaDetalle(null); setCorreoPreview(null); setCargandoCorreo(false);
    setBitacora([]);
    setPolAgentesNombres('');
    const esCobro = r.tipo === 'cobro';
    const [hist, uRes, pagos, bit, polAg, polDet, endRes] = await Promise.all([
      supabase.from('crm_historial').select('*, usuarios(nombre)').eq('gestion_id', r.id).order('creado_en', { ascending:false }),
      supabase.from('usuarios').select('id, nombre').eq('activo', true).order('nombre'),
      r.poliza_id
        ? supabase.from('pagos').select('*').eq('poliza_id', r.poliza_id).order('cuota_num').order('fecha_vencimiento')
        : Promise.resolve({ data: [] }),
      r.poliza_id
        ? supabase.from(esCobro ? 'bitacora_cobros' : 'bitacora_polizas')
            .select('*, usuarios(nombre)')
            .eq('poliza_id', r.poliza_id)
            .order('creado_en', { ascending: false })
        : Promise.resolve({ data: [] }),
      r.poliza_id
        ? supabase.from('poliza_agentes').select('agentes(nombre)').eq('poliza_id', r.poliza_id)
        : Promise.resolve({ data: [] }),
      r.poliza_id
        ? supabase.from('polizas')
            .select('numero, fecha_inicio, fecha_vencimiento, prima, impuesto, aseguradoras(nombre), clientes(nombre, primer_nombre, email)')
            .eq('id', r.poliza_id).maybeSingle()
        : Promise.resolve({ data: null }),
      r.poliza_id
        ? supabase.from('poliza_endosos').select('*').eq('poliza_id', r.poliza_id).order('fecha', { ascending:false })
        : Promise.resolve({ data: [] }),
    ]);
    setPolizaDetalle(polDet?.data || null);
    setHistorial((hist.data || []).map(h => ({ ...h, usuario:h.usuarios?.nombre })));
    setUsuarios(uRes.data || []);
    setPagosPoliza(pagos.data || []);
    // El autor de cada nota se trae por el vínculo (FK) bitacora_*.usuario_id → usuarios.
    setBitacora((bit.data || []).map(b => ({ ...b, usuario_nombre: b.usuarios?.nombre || null })));
    setPolAgentesNombres((polAg.data || []).map(a => a.agentes?.nombre).filter(Boolean).join(', '));
    setEndososTarea(endRes?.data || []);
    setEndosoDocFile(null);
    setModal('view');
  };

  // ── Ciclo del endoso (desde la tarea del CRM, enlazado a la solicitud) ──
  const refrescarHistorial = async () => {
    const { data } = await supabase.from('crm_historial')
      .select('*, usuarios(nombre)').eq('gestion_id', selected.id).order('creado_en', { ascending:false });
    setHistorial((data || []).map(h => ({ ...h, usuario: h.usuarios?.nombre })));
  };

  const ENDOSO_LABEL = { enviada_aseg:'Enviada a aseguradora', endoso_recibido:'Endoso recibido', enviado_cliente:'Enviado al cliente' };

  const avanzarCicloEndoso = async (nextEstado) => {
    if (!selected?.solicitud_id) return;
    // Facturación por partes: no cerrar el endoso ("enviado al cliente") si quedan
    // cambios pendientes de facturar. Hay que recibir/registrar las partes que faltan.
    if (nextEstado === 'enviado_cliente') {
      const { data: solCheck } = await supabase.from('solicitudes').select('cambios_json').eq('id', selected.solicitud_id).maybeSingle();
      const arr = Array.isArray(solCheck?.cambios_json) ? solCheck.cambios_json : [];
      const pend = arr.filter(ch => ch.estado !== 'facturado').length;
      if (pend > 0) {
        alert(`No puedes cerrar el endoso todavía: quedan ${pend} cambio(s) pendiente(s) de facturar. Registra las partes que faltan primero.`);
        return;
      }
    }
    setAvanzandoEndoso(true);
    try {
      await updateRow('solicitudes', selected.solicitud_id, { estado: nextEstado, actualizado_en: new Date().toISOString() });
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'endoso', detalle: `Ciclo del endoso → ${ENDOSO_LABEL[nextEstado] || nextEstado}` });
      setEndosoSolEstado(nextEstado);
      // Enviado al cliente: se cierra la tarea (fin del ciclo).
      if (nextEstado === 'enviado_cliente') {
        await updateRow('crm_gestiones', selected.id, { estado: 'cerrado', actualizado_en: new Date().toISOString() });
        await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'cerrar', detalle: 'Endoso enviado al cliente — tarea cerrada.' });
      }
      await refrescarHistorial();
      await load();
      if (nextEstado === 'enviado_cliente') setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setAvanzandoEndoso(false); }
  };

  // Adjuntar el documento del endoso recibido de la aseguradora (a la solicitud del endoso).
  const subirEndosoRecibido = async () => {
    if (!endosoDocFile || !selected?.solicitud_id) return;
    setSubiendoEndosoDoc(true);
    try {
      const ext = endosoDocFile.name.split('.').pop();
      const path = `${selected.solicitud_id}/endoso_${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('solicitud-archivos').upload(path, endosoDocFile, { upsert:true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
      await insertRow('solicitud_archivos', { solicitud_id: selected.solicitud_id, nombre: endosoDocFile.name, url: publicUrl, tipo: 'solicitud', tipo_doc: 'Endoso recibido' });
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'endoso', detalle: `Adjuntado el endoso recibido: ${endosoDocFile.name}` });
      setEndosoDocUrl(publicUrl); setEndosoDocFile(null); setIaResultado(null);
      await refrescarHistorial();
    } catch (e) { alert('Error al adjuntar: ' + (e.message || '')); }
    finally { setSubiendoEndosoDoc(false); }
  };

  // Enviar al cliente: adjunta el correo (PDF/imagen) O pega el texto del correo, y cierra la tarea.
  const confirmarEnvioCliente = async () => {
    if (!selected?.solicitud_id) return;
    const esTexto = emailModalMode === 'texto';
    if (esTexto && !emailTexto.trim()) return alert('Pega el texto del correo enviado al cliente.');
    if (!esTexto && !emailClienteFile) return alert('Adjunta el PDF/imagen del correo enviado al cliente.');
    setEnviandoCliente(true);
    try {
      let url, nombre;
      if (esTexto) {
        const blob = new Blob([emailTexto], { type: 'text/plain;charset=utf-8' });
        const path = `${selected.solicitud_id}/correo_cliente_${Date.now()}.txt`;
        const { error } = await supabase.storage.from('solicitud-archivos').upload(path, blob, { upsert:true, contentType:'text/plain' });
        if (error) throw error;
        url = supabase.storage.from('solicitud-archivos').getPublicUrl(path).data.publicUrl;
        nombre = 'Correo al cliente (texto).txt';
      } else {
        const ext = emailClienteFile.name.split('.').pop();
        const path = `${selected.solicitud_id}/correo_cliente_${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('solicitud-archivos').upload(path, emailClienteFile, { upsert:true });
        if (error) throw error;
        url = supabase.storage.from('solicitud-archivos').getPublicUrl(path).data.publicUrl;
        nombre = emailClienteFile.name;
      }
      await insertRow('solicitud_archivos', { solicitud_id: selected.solicitud_id, nombre, url, tipo: 'solicitud', tipo_doc: 'Correo al cliente' });
      setEmailModal(false); setEmailClienteFile(null); setEmailTexto('');
      // Avanzar el ciclo → cierra la tarea (registra el adjunto en el log más el cierre).
      await avanzarCicloEndoso('enviado_cliente');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setEnviandoCliente(false); }
  };

  // Validar con IA que el endoso recibido coincide con lo solicitado (doble validación).
  const analizarEndosoIA = async () => {
    if (!selected?.solicitud_id) return;
    setAnalizandoIA(true); setIaResultado(null);
    try {
      // Traer la solicitud (lo pedido) + el documento del endoso recibido.
      // Nota: el nombre del tipo se resuelve por lookup (NO hay FK/embed solicitudes→catalogo_endoso_tipos).
      const { data: sol } = await supabase.from('solicitudes')
        .select('prima, notas, poliza_endoso_id, tipo_endoso_id, cambios_json').eq('id', selected.solicitud_id).maybeSingle();
      let tipoNombre = '';
      if (sol?.tipo_endoso_id) {
        const { data: t } = await supabase.from('catalogo_endoso_tipos').select('nombre').eq('id', sol.tipo_endoso_id).maybeSingle();
        tipoNombre = t?.nombre || '';
      }
      // Lista de cambios solicitados (multi-cambio) para que la IA compare el documento
      // recibido contra TODO lo pedido y señale qué corresponde y qué falta (pendiente).
      const cj = sol?.cambios_json;
      const cambiosArr = Array.isArray(cj) ? cj : (cj && typeof cj === 'object' ? [{ tipo_endoso_id: sol.tipo_endoso_id, cambios: cj }] : []);
      let cambiosTexto = '';
      if (cambiosArr.length) {
        const ids = [...new Set(cambiosArr.map(c => c.tipo_endoso_id).filter(Boolean))];
        const { data: cats } = ids.length
          ? await supabase.from('catalogo_endoso_tipos').select('id, nombre').in('id', ids)
          : { data: [] };
        const nombreDe = id => (cats || []).find(t => String(t.id) === String(id))?.nombre || 'Cambio';
        cambiosTexto = cambiosArr.map((c, i) => {
          const det = c.cambios && typeof c.cambios === 'object'
            ? Object.entries(c.cambios)
                .filter(([k, v]) => v !== '' && v != null && k !== 'vehiculo_id' && k !== 'cobertura_id')
                .map(([k, v]) => `${k}=${v}`).join(', ')
            : '';
          const est = c.estado === 'facturado' ? ' [ya facturado]' : '';
          return `${i + 1}) ${nombreDe(c.tipo_endoso_id)}${det ? ': ' + det : ''}${est}`;
        }).join('  ·  ');
      }
      const { data: arch } = await supabase.from('solicitud_archivos')
        .select('url, nombre, tipo_doc').eq('solicitud_id', selected.solicitud_id).eq('tipo_doc', 'Endoso recibido')
        .order('creado_en', { ascending:false }).limit(1);
      const doc = arch?.[0];
      if (!doc) { setIaResultado({ error: 'Primero adjunta el endoso recibido.' }); return; }
      // Descargar el documento y pasarlo a base64
      const resp = await fetch(doc.url);
      const blob = await resp.blob();
      const media_type = blob.type || (/\.pdf$/i.test(doc.nombre || '') ? 'application/pdf' : 'image/jpeg');
      const dataB64 = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(',')[1]);
        fr.onerror = rej; fr.readAsDataURL(blob);
      });
      const r = await fetch('/api/analizar-endoso', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({
          media_type, data: dataB64,
          solicitado: {
            tipo_endoso: tipoNombre,
            descripcion: [sol?.notas || '', cambiosTexto ? `Cambios solicitados: ${cambiosTexto}` : '']
              .filter(Boolean).join(' | '),
            prima: sol?.prima ?? '',
            poliza: selected.poliza_num || '',
            cliente: selected.cliente_nombre || '',
          },
        }),
      });
      const out = await r.json().catch(() => null);
      if (!out?.ok) {
        // El mensaje debe decir POR QUÉ falló (incl. cupo agotado del plan).
        setIaResultado({ error:
          out?.reason === 'no_configurado' ? 'La IA aún no está activada en tu sistema.'
          : out?.reason === 'cupo' ? (out?.detalle || 'Llegaste al límite de análisis con IA de tu plan este mes.')
          : 'No se pudo analizar el endoso.' + (out?.detalle ? ` (${out.detalle})` : '') });
        return;
      }
      setIaResultado(out.resultado || { error: 'Sin resultado.' });
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'endoso',
        detalle: `Análisis IA del endoso: ${out.resultado?.coincide ? 'coincide' : 'NO coincide'}${out.resultado?.resumen ? ' — ' + out.resultado.resumen : ''}` });
      await refrescarHistorial();
    } catch (e) { setIaResultado({ error: 'Error al analizar: ' + (e.message || '') }); }
    finally { setAnalizandoIA(false); }
  };

  // Reversar el ciclo del endoso (SOLO superusuario): ELIMINA el/los endosos registrados por esta
  // solicitud (sin dejar rastro anulado), revierte prima/cuotas/comisiones (misma lógica que Anular),
  // borra los documentos del ciclo y regresa la tarea a "Enviada a aseguradora".
  const reversarCicloEndoso = async () => {
    if (!user?.es_master || !selected?.solicitud_id) return;
    if (!window.confirm('¿Reversar el ciclo del endoso?\n\nSe ELIMINA el endoso registrado (sin dejar registro anulado), se revierten prima/cuotas/comisiones, se borran los documentos adjuntos y la tarea vuelve a "Enviada a aseguradora". Acción solo para superusuarios.')) return;
    setReversando(true);
    try {
      const { data: sol } = await supabase.from('solicitudes').select('poliza_endoso_id').eq('id', selected.solicitud_id).maybeSingle();
      const polizaId = sol?.poliza_endoso_id;
      // Endosos registrados por esta solicitud (original + posibles correcciones)
      const { data: endosos } = await supabase.from('poliza_endosos').select('id, tipo').eq('solicitud_id', selected.solicitud_id);
      for (const e of (endosos || [])) {
        // 1) revertir la dilución en las cuotas
        const { data: ajustes } = await supabase.from('poliza_endoso_ajustes').select('id, pago_id, monto').eq('endoso_id', e.id);
        for (const aj of (ajustes || [])) {
          const { data: pg } = await supabase.from('pagos').select('monto').eq('id', aj.pago_id).maybeSingle();
          if (pg) {
            const nuevo = Math.round(((parseFloat(pg.monto) || 0) - (parseFloat(aj.monto) || 0)) * 100) / 100;
            await updateRow('pagos', aj.pago_id, { monto: nuevo });
          }
        }
        if (ajustes && ajustes.length) await supabase.from('poliza_endoso_ajustes').delete().eq('endoso_id', e.id);
        // 2) borrar letras sueltas del endoso + sus cobro_cuotas y comisiones
        const { data: pagosEnd } = await supabase.from('pagos').select('id').eq('endoso_id', e.id);
        const ids = (pagosEnd || []).map(p => p.id);
        if (ids.length) {
          await supabase.from('cobro_cuotas').delete().in('pago_id', ids);
          await supabase.from('comisiones').delete().in('pago_id', ids);
          await supabase.from('pagos').delete().in('id', ids);
        }
        // 3) ELIMINAR el endoso (sin dejar rastro anulado)
        await supabase.from('poliza_endosos').delete().eq('id', e.id);
        // 3b) si era cancelación, reactivar la póliza
        if (e.tipo === 'cancelacion' && polizaId) await updateRow('polizas', polizaId, { estado: 'activa' });
      }
      // 4) redistribuir el saco de la póliza
      if (polizaId) { try { await reaplicarSacoPoliza(polizaId); } catch (er) { console.error('saco', er); } }
      // 5) borrar los documentos del ciclo
      await supabase.from('solicitud_archivos').delete().eq('solicitud_id', selected.solicitud_id).in('tipo_doc', ['Endoso recibido', 'Correo al cliente']);
      // 6) regresar la solicitud a "Enviada a aseguradora"
      await updateRow('solicitudes', selected.solicitud_id, { estado: 'enviada_aseg', actualizado_en: new Date().toISOString() });
      await insertRow('crm_historial', { gestion_id: selected.id, usuario_id: user?.id, accion: 'endoso', detalle: 'Ciclo del endoso REVERSADO (superusuario): endoso eliminado, documentos borrados, vuelve a "Enviada a aseguradora".' });
      setEndosoSolEstado('enviada_aseg'); setEndosoDocUrl(null); setIaResultado(null); setConfirmoEndoso(false);
      await refrescarHistorial();
      await load();
      alert('Ciclo del endoso reversado. La tarea volvió a "Enviada a aseguradora".');
    } catch (e) { alert('Error al reversar: ' + (e.message || '')); }
    finally { setReversando(false); }
  };

  // Ir al flujo probado de "Registrar Endoso" (facturar) en Solicitudes para aplicar el endoso a la póliza.
  const irARegistrarEndoso = () => {
    if (!selected?.solicitud_id) return;
    sessionStorage.setItem('openConvertSolicitudId', String(selected.solicitud_id));
    sessionStorage.setItem('openCrmTaskId', String(selected.id)); // para volver a esta tarea al terminar
    sessionStorage.setItem('backFrom', 'crm');
    navigate('/solicitudes');
  };

  const [notaPosponer, setNotaPosponer] = useState('');

  const handlePosponer = async () => {
    if (!fechaPosponer) return alert('Selecciona la fecha hasta la que se pospone.');
    if (!notaPosponer.trim()) return alert('Debes escribir un motivo para posponer la tarea.');
    try {
      await updateRow('crm_gestiones', selected.id, {
        posponer_hasta: fechaPosponer,
        actualizado_en: new Date().toISOString(),
      });
      // La gestión del día = comentario + acción, en una sola línea de bitácora.
      const detalle = `${notaPosponer.trim()} — ⏸ Pospuesto hasta ${fmtFecha(fechaPosponer)}`;
      await insertRow('crm_historial', {
        gestion_id: selected.id, usuario_id: user?.id,
        accion: 'posponer',
        detalle,
      });
      // Si tiene póliza, también queda en la bitácora
      if (selected.poliza_id) {
        const tabla = selected.tipo === 'cobro' ? 'bitacora_cobros' : 'bitacora_polizas';
        await insertRow(tabla, {
          poliza_id: selected.poliza_id, usuario_id: user?.id,
          nota: detalle,
          ...(selected.tipo !== 'cobro' && { tipo: 'seguimiento' }),
        });
      }
      setShowPosponer(false); setFechaPosponer(''); setNotaPosponer('');
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) return;
    try {
      const payload = { ...form };
      ['cliente_id','poliza_id','agente_id','asignado_a'].forEach(k => {
        payload[k] = payload[k] ? parseInt(payload[k]) : null;
      });
      if (!payload.fecha_vencimiento) payload.fecha_vencimiento = null;
      if (payload.asignado_a) {
        payload.asignado_por = user?.id || null;
      }
      await insertRow('crm_gestiones', payload);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const cerrar = async (r) => {
    try {
      await updateRow('crm_gestiones', r.id, { estado:'cerrado', actualizado_en: new Date().toISOString() });
      await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const handleAsignar = async () => {
    if (!asignandoA) return;
    if (!motivoAsig.trim()) { alert('Debes escribir el comentario de la reasignación.'); return; }
    try {
      const nombreDestino = usuarios.find(u=>String(u.id)===String(asignandoA))?.nombre || asignandoA;
      await updateRow('crm_gestiones', selected.id, {
        asignado_a: parseInt(asignandoA),
        asignado_por: user?.id || null,
        asignado_manual: true,  // reasignación manual: el sync automático ya no la toca
        motivo_asignacion: motivoAsig || null,
        actualizado_en: new Date().toISOString(),
      });
      // La gestión del día = comentario + acción, en una sola línea de bitácora.
      const detalle = `${motivoAsig.trim()} — 👤 Reasignado a ${nombreDestino}`;
      await insertRow('crm_historial', {
        gestion_id: selected.id, usuario_id: user?.id,
        accion: 'asignacion',
        detalle,
      });
      // Si tiene póliza, la reasignación también queda en la bitácora (igual que posponer).
      if (selected.poliza_id) {
        const tabla = selected.tipo === 'cobro' ? 'bitacora_cobros' : 'bitacora_polizas';
        await insertRow(tabla, {
          poliza_id: selected.poliza_id, usuario_id: user?.id,
          nota: detalle,
          ...(selected.tipo !== 'cobro' && { tipo: 'seguimiento' }),
        });
      }
      setShowAsignar(false); setAsignandoA(''); setMotivoAsig('');
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const hoy = new Date().toISOString().split('T')[0];
  // Último día del mes en curso: el cliente paga al inicio de cada periodo, así que
  // la gestión de cobro incluye lo vencido + la cuota cuyo periodo arranca este mes.
  const finMes = (() => {
    const d = new Date();
    const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
  })();
  const vencidas = rows.filter(r => r.fecha_vencimiento && r.fecha_vencimiento < hoy && r.estado === 'abierto').length;
  const meTocaA = (r) => !r.asignado_a || r.asignado_a === user?.id;

  // ── Orden por columna (clic en el encabezado: asc → desc → asc …) ──
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const sortVal = (r, col) => {
    switch (col) {
      case 'prioridad':    return PRIORIDADES.indexOf(r.prioridad);          // baja<normal<alta<urgente
      case 'tipo':         return (r.tipo || '').toLowerCase();
      case 'titulo':       return (r.titulo || '').toLowerCase();
      case 'cliente':      return (r.cliente_nombre || '').toLowerCase();
      case 'poliza_ejecutivo': return (r.poliza_ejecutivo || '').toLowerCase();
      case 'poliza_agentes':   return (r.poliza_agentes || '').toLowerCase();
      case 'asignado':     return (r.asignado_nombre || '').toLowerCase();
      case 'vencimiento':  return r.fecha_vencimiento || '';
      case 'estado':       return (r.estado || '').toLowerCase();
      default:             return '';
    }
  };
  const sortedRows = (() => {
    const arr = [...rows];
    if (!sortCol) {
      // Orden por defecto: prioridad ALTA primero (urgente>alta>normal>baja) y,
      // a igual prioridad, la MÁS ANTIGUA primero (se prioriza lo viejo).
      arr.sort((a, b) => {
        const pa = PRIORIDADES.indexOf(a.prioridad), pb = PRIORIDADES.indexOf(b.prioridad);
        if (pa !== pb) return pb - pa;
        return String(a.fecha_vencimiento || '').localeCompare(String(b.fecha_vencimiento || ''));
      });
      return arr;
    }
    arr.sort((a, b) => {
      const va = sortVal(a, sortCol), vb = sortVal(b, sortCol);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  })();

  const [generando, setGenerando] = useState(false);
  const [resumenGen, setResumenGen] = useState(null);
  const [generandoRenov, setGenerandoRenov] = useState(false);

  const generarSolicitudRenovacion = async () => {
    if (!selected?.poliza_id) { alert('Esta tarea no tiene póliza vinculada.'); return; }
    setGenerandoRenov(true);
    try {
      // Cargar datos completos de la póliza
      const { data: pol } = await supabase.from('polizas')
        .select('*, clientes(nombre), ramos(nombre), aseguradoras(nombre)')
        .eq('id', selected.poliza_id).single();
      if (!pol) throw new Error('No se encontró la póliza.');

      // Verificar si ya existe una solicitud de renovación abierta PARA ESTA PÓLIZA.
      // El vínculo es `poliza_endoso_id` (la póliza que se está renovando), NO el cliente:
      // un cliente con varias pólizas venciendo debe poder renovarlas todas.
      const { data: solExist } = await supabase.from('solicitudes')
        .select('id').eq('tipo','renovacion')
        .eq('poliza_endoso_id', pol.id)
        .not('estado','in','(convertida,rechazada,cancelada)')
        .limit(1);
      if (solExist?.length) {
        alert('Ya existe una solicitud de renovación activa para esta póliza (SOL-' + String(solExist[0].id).padStart(4,'0') + ').');
        setGenerandoRenov(false); return;
      }

      // Cargar agentes, coberturas y vehículo de la póliza para copiarlos a la renovación
      const [{ data: polAgentes }, { data: polCobs }, { data: polAuto }] = await Promise.all([
        supabase.from('poliza_agentes').select('agente_id, porcentaje, comision_manual').eq('poliza_id', pol.id),
        supabase.from('poliza_coberturas').select('*').eq('poliza_id', pol.id).order('id'),
        supabase.from('poliza_autos').select('*').eq('poliza_id', pol.id).maybeSingle(),
      ]);

      // Nueva vigencia: arranca donde terminó la anterior, un año.
      const fiRenov = pol.fecha_vencimiento || null;
      const fvRenov = fiRenov ? (() => { const d = new Date(fiRenov + 'T12:00:00'); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); })() : null;

      // Crear solicitud de renovación pre-poblada con TODO lo de la póliza anterior
      const nuevaSol = await insertRow('solicitudes', {
        cliente_id:       pol.cliente_id,
        aseguradora_id:   pol.aseguradora_id,
        ramo_id:          pol.ramo_id,
        subramo_id:       pol.subramo_id || null,
        ejecutivo_id:     pol.ejecutivo_id || null,
        agente_id:        pol.agente_id || null,
        tipo:             'renovacion',
        prima:            pol.prima,
        prima_estimada:   pol.prima,
        impuesto:         pol.impuesto,
        comision_ramo:    pol.comision_ramo,
        es_fee:           pol.es_fee || false,
        monto_fee:        pol.monto_fee || null,
        forma_pago:       pol.forma_pago,
        frecuencia_pago:  pol.frecuencia_pago,
        num_cuotas:       pol.num_cuotas,
        fecha_inicio:     fiRenov,
        fecha_vencimiento:fvRenov,
        referido_id:      pol.referido_id || null,
        referido_comision_pct: pol.referido_comision_pct ?? null,
        estado:           'enviada',
        notas:            `Renovación de póliza ${pol.numero}. Vencimiento anterior: ${pol.fecha_vencimiento}.`,
        poliza_endoso_id: pol.id,  // referencia a la póliza original
      });

      // Copiar agentes
      for (const ag of (polAgentes || [])) {
        await insertRow('solicitud_agentes', {
          solicitud_id: nuevaSol.id,
          agente_id: ag.agente_id,
          porcentaje: ag.porcentaje || 0,
        });
      }

      // Copiar coberturas
      for (const c of (polCobs || [])) {
        await insertRow('solicitud_coberturas', {
          solicitud_id: nuevaSol.id,
          cobertura_id: c.cobertura_id || null,
          nombre: c.nombre || '',
          tipo_limite: c.tipo_limite || 'limite',
          suma_asegurada: c.suma_asegurada ?? null,
          deducible: c.deducible ?? null,
          otros: c.otros ?? null,
        });
      }

      // Copiar datos del vehículo (si la póliza tiene)
      if (polAuto) {
        await insertRow('solicitud_autos', {
          solicitud_id: nuevaSol.id,
          anio: polAuto.anio ?? null, marca: polAuto.marca ?? null, modelo: polAuto.modelo ?? null,
          color: polAuto.color ?? null, tipo_vehiculo: polAuto.tipo_vehiculo ?? null, pasajeros: polAuto.pasajeros ?? null,
          no_motor: polAuto.no_motor ?? null, chasis: polAuto.chasis ?? null, placa: polAuto.placa ?? null,
        });
      }

      // Actualizar tarea CRM: marcar como "en_proceso" y ENLAZAR la solicitud creada.
      // Ese vínculo permite reabrir la tarea si la solicitud se elimina por error.
      await updateRow('crm_gestiones', selected.id, {
        estado: 'en_proceso',
        solicitud_id: nuevaSol.id,
        actualizado_en: new Date().toISOString(),
      });
      await insertRow('crm_historial', {
        gestion_id: selected.id,
        usuario_id: user?.id,
        accion: 'nota',
        detalle: `✅ Cliente acepta renovación. Solicitud SOL-${String(nuevaSol.id).padStart(4,'0')} generada automáticamente.`,
      });

      // Registrar en bitácora de póliza
      await insertRow('bitacora_polizas', {
        poliza_id: pol.id,
        usuario_id: user?.id,
        nota: `Cliente acepta renovación. Solicitud SOL-${String(nuevaSol.id).padStart(4,'0')} creada.`,
        tipo: 'renovacion',
      });

      await load(); setModal(null);
      // Abrir la solicitud recién creada directo en pantalla, para que el ejecutivo la
      // gestione sin tener que ir a buscarla a Solicitudes.
      sessionStorage.setItem('openSolicitudId', String(nuevaSol.id));
      sessionStorage.setItem('backFrom', 'crm');
      navigate('/solicitudes');
    } catch(e) { alert('Error: ' + (e.message||'')); }
    finally { setGenerandoRenov(false); }
  };

  const generarTareasAutomaticas = async () => {
    setGenerando(true); setResumenGen(null);
    let creadas = 0;
    let actualizadas = 0;
    try {
      const en60 = new Date(); en60.setDate(en60.getDate() + 60);
      const en60Str = en60.toISOString().slice(0,10);

      // ── Mapas de asignación ──
      const cfg = await getConfig();
      const respCobros   = cfg?.responsable_cobros_usuario_id   || null;
      const respReclamos = cfg?.responsable_reclamos_usuario_id || null;
      const { data: usuariosEjec } = await supabase.from('usuarios')
        .select('id, ejecutivo_id').eq('activo', true).not('ejecutivo_id', 'is', null);
      const ejecToUser = {};
      (usuariosEjec || []).forEach(u => { ejecToUser[u.ejecutivo_id] = u.id; });
      const userDeEjecutivo = (ejecutivo_id) => ejecutivo_id ? (ejecToUser[ejecutivo_id] || null) : null;

      // ── UNA SOLA foto de las tareas abiertas ──
      // Reemplaza los cientos de consultas "¿ya existe esta tarea?" (patrón N+1) por
      // búsquedas en memoria. Las escrituras también se agrupan (insert en bloque,
      // cierres con un solo UPDATE ... IN) para no ir de a una a la base.
      const { data: abiertasAll } = await supabase.from('crm_gestiones')
        .select('id, tipo, poliza_id, solicitud_id, reclamo_id, titulo, descripcion, prioridad, fecha_vencimiento')
        .in('estado', ['abierto', 'en_proceso']);
      const abiertas = abiertasAll || [];
      const tareaPorPolTipo   = new Map(); // `${poliza_id}|${tipo}` -> tarea (renovación/cobro)
      const tareaPorSolicitud = new Map(); // solicitud_id -> tarea (endoso/cotización)
      const tareaPorReclamo   = new Map(); // reclamo_id -> tarea
      for (const t of abiertas) {
        if (t.poliza_id)    tareaPorPolTipo.set(`${t.poliza_id}|${t.tipo}`, t);
        if (t.solicitud_id) tareaPorSolicitud.set(t.solicitud_id, t);
        if (t.reclamo_id)   tareaPorReclamo.set(t.reclamo_id, t);
      }

      const nuevasTareas = [];      // se insertan todas juntas al final
      const updatesTexto = [];      // promesas de update de texto (cobros ya existentes)
      const idsCerrar    = new Set(); // se cierran todas juntas al final
      const histDuplicadas = [];      // notas de historial de los cierres automáticos por duplicado (6.b-c)

      // 0. Obligaciones y recomendaciones contables (a 60 días) → solo si hay
      //    un responsable de contabilidad designado; se le asignan a esa persona.
      try {
        creadas += await generarGestionesContables(cfg, 60);
      } catch { /* no interrumpir la generación normal del CRM */ }

      // 0.b Facturación periódica: crea los períodos pendientes de colectivos/declarativas
      //     y su tarea de "pedir/registrar la factura del período" al ejecutivo de cuenta.
      try {
        const rp = await generarPeriodosPendientes();
        creadas += rp?.creados || 0;
      } catch { /* no interrumpir la generación normal del CRM */ }

      // 0.c Declarativas ACUMULATIVAS: recordatorio anual de la regularización de cierre
      //     al acercarse el vencimiento (la persona la genera con un clic).
      try {
        const rr = await generarRecordatoriosRegularizacion();
        creadas += rr?.creados || 0;
      } catch { /* no interrumpir la generación normal del CRM */ }

      // Texto de una tarea de renovación. Se usa al CREARLA y al REFRESCARLA (sección 6.b):
      // el mismo molde en los dos lados, para que el refresco no reescriba por gusto.
      // Antes el texto era una FOTO del día que se creó la tarea y nunca se recalculaba:
      // meses después seguía diciendo "vence en 5 días" con la fecha ya pasada.
      const textoRenovacion = (numero, cliente, fechaVenc) => {
        const fv = fechaVenc ? String(fechaVenc).slice(0, 10) : null;
        const dias = fv
          ? Math.ceil((new Date(fv + 'T12:00:00') - new Date(hoy + 'T12:00:00')) / 86400000)
          : null;
        const cuando = dias === null ? 'no tiene fecha de vencimiento registrada'
          : dias  >  0 ? `vence el ${fv} (${dias} día(s))`
          : dias === 0 ? `vence HOY (${fv})`
          :              `venció hace ${-dias} día(s) (${fv}) y sigue sin renovar`;
        return {
          dias,
          titulo: `Renovar póliza ${numero || ''}`.trim(),
          descripcion: `La póliza ${numero || ''} del cliente ${cliente || ''} ${cuando}. Contactar para gestionar renovación.`,
          prioridad: (dias === null || dias <= 30) ? 'alta' : 'normal',
        };
      };

      // 1. Pólizas que vencen en ≤ 60 días → tarea de renovación (al ejecutivo de cuenta)
      const { data: porVencer } = await supabase.from('polizas')
        .select('id, numero, cliente_id, ejecutivo_id, clientes(nombre), fecha_vencimiento')
        .in('estado', ['activa', 'en_renovacion'])
        .lte('fecha_vencimiento', en60Str)
        .gte('fecha_vencimiento', hoy);

      // ── ¿Cuáles de estas pólizas YA se renovaron? ──
      // Se resuelve UNA vez y sirve para dos cosas: no crear tarea de renovación de algo
      // ya renovado (abajo) y cerrar las que quedaron abiertas (sección 6.b). Sin esto se
      // haría un ciclo: 6.b cierra la tarea y la sección 1 la vuelve a crear, porque la
      // vigencia vieja sigue 'activa' hasta el día que vence aunque ya exista la nueva.
      // Criterio = el MISMO del módulo Renovaciones (`clasificar` en RenovacionesView):
      // otra póliza la apunta (renovacion_de_poliza_id = mapa, poliza_anterior_id =
      // arrastre de saco) o tiene una solicitud de renovación ya convertida.
      const renovAbiertas = abiertas.filter(t => t.tipo === 'renovacion' && t.poliza_id);
      const renovPolIds   = [...new Set(renovAbiertas.map(t => t.poliza_id))];
      const polIdsRenov   = [...new Set([...(porVencer || []).map(p => p.id), ...renovPolIds])];
      const yaRenovada    = new Set();
      if (polIdsRenov.length) {
        const [{ data: apuntan }, { data: solsRenov }] = await Promise.all([
          supabase.from('polizas').select('id, renovacion_de_poliza_id, poliza_anterior_id')
            .or(`renovacion_de_poliza_id.in.(${polIdsRenov.join(',')}),poliza_anterior_id.in.(${polIdsRenov.join(',')})`),
          supabase.from('solicitudes').select('poliza_endoso_id')
            .eq('tipo', 'renovacion').eq('estado', 'convertida').in('poliza_endoso_id', polIdsRenov),
        ]);
        for (const p of apuntan || []) {
          // El self-reference (una póliza que se apunta a sí misma) es un error de datos
          // conocido, NO una renovación: no debe contar.
          if (p.renovacion_de_poliza_id && p.renovacion_de_poliza_id !== p.id) yaRenovada.add(Number(p.renovacion_de_poliza_id));
          if (p.poliza_anterior_id      && p.poliza_anterior_id      !== p.id) yaRenovada.add(Number(p.poliza_anterior_id));
        }
        for (const s of solsRenov || []) { if (s.poliza_endoso_id) yaRenovada.add(Number(s.poliza_endoso_id)); }
      }

      // ── ¿En cuáles alguien YA DECIDIÓ "Cliente no renueva"? ──
      // Ese cierre a mano solo cerraba la tarea: la póliza sigue 'activa' hasta que vence,
      // así que esta sección la veía "sin tarea abierta" y la volvía a crear en cada
      // "Actualizar tareas" (caso 02BR2925416: Gynnela la cerró dos veces el mismo día).
      // La decisión vive en el historial de la tarea cerrada (accion 'cerrar', detalle
      // "[Cliente no renueva] …", ver handleCerrarConMotivo). Vale SOLO para la vigencia en
      // que se tomó: se compara el vencimiento de esa tarea con el de la póliza, para que
      // una póliza renovada sobre el mismo registro el año siguiente sí reciba su tarea.
      // Lector compartido con Pólizas y Renovaciones (src/db/renovaciones.js).
      const noRenuevaFV = await decisionesNoRenueva(polIdsRenov);
      const decidioNoRenovar = (polId, fv) => decidioNoRenovarEn(noRenuevaFV, polId, fv);

      for (const pol of porVencer || []) {
        if (tareaPorPolTipo.has(`${pol.id}|renovacion`)) continue;
        if (yaRenovada.has(Number(pol.id))) continue;  // ya tiene su vigencia siguiente
        if (decidioNoRenovar(pol.id, pol.fecha_vencimiento)) continue;  // ya se decidió: no renueva
        const t = textoRenovacion(pol.numero || pol.id, pol.clientes?.nombre, pol.fecha_vencimiento);
        nuevasTareas.push({
          tipo: 'renovacion', prioridad: t.prioridad,
          titulo: t.titulo,
          descripcion: t.descripcion,
          poliza_id: pol.id, cliente_id: pol.cliente_id,
          asignado_a: userDeEjecutivo(pol.ejecutivo_id),
          estado: 'abierto',
          fecha_vencimiento: pol.fecha_vencimiento,
        });
      }

      // 2. Pagos vencidos → UNA tarea de cobro por póliza (al ejecutivo de cobro).
      //    Se agrupan las cuotas vencidas por póliza (regla del SACO: lo que importa
      //    es la más antigua) y, si ya hay una tarea de cobro abierta, se REFRESCA su
      //    texto a la deuda real en lugar de saltarla. Así el texto nunca queda
      //    congelado cuando el plan de pagos se corrige (p. ej. de prima única a cuotas).
      //    Incluye 'parcial': una cuota a medio pagar sigue debiéndose.
      //    Incluye lo vencido Y lo que vence este mes (hasta finMes): el cliente paga al
      //    inicio de cada periodo, así que la cuota del mes en curso ya es por cobrar
      //    (para recordarle antes de que se atrase), no solo las ya vencidas.
      const { data: pagosVencidos } = await supabase.from('pagos')
        .select('id, poliza_id, monto, monto_pagado, fecha_vencimiento, polizas(numero, estado, cliente_id, ejecutivo_cobro_id, clientes(nombre))')
        .in('estado', ['pendiente', 'parcial'])
        .lte('fecha_vencimiento', finMes);

      const cobroPorPoliza = {};
      const polizasConDeuda = new Set(); // reutilizado por la sección 6 (auto-cierre)
      for (const pago of pagosVencidos || []) {
        (cobroPorPoliza[pago.poliza_id] = cobroPorPoliza[pago.poliza_id] || []).push(pago);
        polizasConDeuda.add(Number(pago.poliza_id));
      }

      for (const polId of Object.keys(cobroPorPoliza)) {
        const cuotas = cobroPorPoliza[polId]
          .sort((a, b) => String(a.fecha_vencimiento).localeCompare(String(b.fecha_vencimiento)));
        const masAntigua = cuotas[0];
        const pol = masAntigua.polizas;
        // Póliza cancelada: no se gestiona cobro (la sección 6 cierra la tarea si existe).
        if (pol?.estado === 'cancelada') continue;
        const saldoDe = (p) => Number(p.monto || 0) - Number(p.monto_pagado || 0);
        const saldoAntigua = saldoDe(masAntigua);
        const totalVencido = cuotas.reduce((s, p) => s + saldoDe(p), 0);
        const diasVenc = Math.floor((new Date() - new Date(masAntigua.fecha_vencimiento)) / 86400000);
        const estadoTxt = diasVenc > 0 ? `vencida hace ${diasVenc} día(s)`
          : (diasVenc === 0 ? 'vence hoy' : `vence en ${-diasVenc} día(s)`);
        const cliente = pol?.clientes?.nombre || '';
        const titulo = `Cobro pendiente — Póliza ${pol?.numero || polId}`;
        const descripcion = cuotas.length > 1
          ? `${cuotas.length} cuota(s) por cobrar (total $${totalVencido.toFixed(2)}). La más antigua ($${saldoAntigua.toFixed(2)}) ${estadoTxt} (${masAntigua.fecha_vencimiento}). Cliente: ${cliente}. Gestionar cobro.`
          : `Cuota de $${saldoAntigua.toFixed(2)} — ${estadoTxt} (${masAntigua.fecha_vencimiento}). Cliente: ${cliente}. Gestionar cobro.`;
        const prioridad = diasVenc > 30 ? 'alta' : 'normal';

        const existe = tareaPorPolTipo.get(`${polId}|cobro`);
        if (existe) {
          // Refrescar solo el texto a la deuda actual (no se toca asignado_a ni asignado_manual).
          updatesTexto.push(updateRow('crm_gestiones', existe.id, { titulo, descripcion, prioridad }));
          actualizadas++;
        } else {
          nuevasTareas.push({
            tipo: 'cobro', prioridad, titulo, descripcion,
            poliza_id: Number(polId), cliente_id: pol?.cliente_id || null,
            asignado_a: userDeEjecutivo(pol?.ejecutivo_cobro_id) || respCobros,
            estado: 'abierto',
            fecha_vencimiento: hoy,
          });
        }
      }

      // 3. Endosos solicitados pendientes de facturar → tarea (al ejecutivo de cuenta de la póliza)
      const { data: endosoSols } = await supabase.from('solicitudes')
        .select('id, poliza_endoso_id, estado, notas, cliente_id')
        .eq('tipo', 'endoso');
      // 'enviado_cliente' = ciclo terminado (endoso enviado al cliente): la tarea ya se cerró y NO se regenera.
      const pendEndoso = (endosoSols || []).filter(s => s.poliza_endoso_id && !['convertida','cancelada','rechazada','enviado_cliente'].includes(s.estado));
      const endPolIds = [...new Set(pendEndoso.map(s => s.poliza_endoso_id))];
      const endPolMap = {};
      if (endPolIds.length) {
        const { data: eps } = await supabase.from('polizas').select('id, numero, ejecutivo_id').in('id', endPolIds);
        (eps || []).forEach(p => { endPolMap[p.id] = p; });
      }
      for (const s of pendEndoso) {
        if (tareaPorSolicitud.has(s.id)) continue;
        const pol = endPolMap[s.poliza_endoso_id];
        nuevasTareas.push({
          tipo: 'endoso', prioridad: 'normal',
          titulo: `Endoso pendiente de facturar — Póliza ${pol?.numero || s.poliza_endoso_id}`,
          descripcion: `Solicitud de endoso SOL-${String(s.id).padStart(4,'0')} pendiente de facturar.${s.notas ? ' ' + s.notas : ''}`,
          poliza_id: s.poliza_endoso_id, cliente_id: s.cliente_id, solicitud_id: s.id,
          asignado_a: userDeEjecutivo(pol?.ejecutivo_id),
          estado: 'abierto', fecha_vencimiento: hoy,
        });
      }

      // 4. Cotizaciones pendientes de respuesta → tarea (a quien creó la solicitud)
      const { data: cotiSols } = await supabase.from('solicitudes')
        .select('id, estado, creado_por, cliente_id, clientes(nombre), ramos(nombre)')
        .eq('tipo', 'cotizacion');
      const pendCoti = (cotiSols || []).filter(s => !['convertida','aceptada','rechazada','cancelada'].includes(s.estado));
      for (const s of pendCoti) {
        if (tareaPorSolicitud.has(s.id)) continue;
        nuevasTareas.push({
          tipo: 'cotizacion', prioridad: 'normal',
          titulo: `Seguimiento cotización — ${s.clientes?.nombre || 'Cliente'}`,
          descripcion: `Cotización SOL-${String(s.id).padStart(4,'0')}${s.ramos?.nombre ? ' (' + s.ramos.nombre + ')' : ''} pendiente de respuesta del cliente.`,
          cliente_id: s.cliente_id, solicitud_id: s.id,
          asignado_a: s.creado_por || null,
          estado: 'abierto', fecha_vencimiento: hoy,
        });
      }

      // 5. Reclamos abiertos → tarea de seguimiento (al ejecutivo de reclamo de la póliza)
      const { data: reclamosAbiertos } = await supabase.from('reclamos')
        .select('id, numero, estado, cliente_id, poliza_id, clientes(nombre), polizas(ejecutivo_reclamo_id)')
        .in('estado', ['abierto','en_proceso','en_revision']);
      for (const rec of reclamosAbiertos || []) {
        if (tareaPorReclamo.has(rec.id)) continue;
        nuevasTareas.push({
          tipo: 'reclamo', prioridad: 'alta',
          titulo: `Reclamo ${rec.numero || rec.id} — seguimiento`,
          descripcion: `Reclamo ${rec.numero || ''} del cliente ${rec.clientes?.nombre || ''} en estado "${rec.estado}". Dar seguimiento con la aseguradora.`,
          cliente_id: rec.cliente_id, poliza_id: rec.poliza_id, reclamo_id: rec.id,
          asignado_a: userDeEjecutivo(rec.polizas?.ejecutivo_reclamo_id) || respReclamos,
          estado: 'abierto', fecha_vencimiento: hoy,
        });
      }

      // 6. Auto-cerrar tareas de cobro cuya póliza ya no tiene cuotas por cobrar este
      //    mes (ni vencidas ni del periodo actual), O cuya póliza fue cancelada.
      //    Se resuelve en memoria: `polizasConDeuda` ya trae las pólizas con saldo (sección 2)
      //    y el estado de las pólizas de esas tareas se pide en UNA sola consulta.
      const cobroAbiertas = abiertas.filter(t => t.tipo === 'cobro' && t.poliza_id);
      const cobroPolIds = [...new Set(cobroAbiertas.map(t => t.poliza_id))];
      // Un solo viaje por las pólizas de AMBAS secciones (6 y 6.b). La 6 solo necesita el
      // estado; la 6.b además el número, el vencimiento y el cliente para refrescar el texto.
      const estadoPol = {};
      const polInfo = {};
      const polIdsEstado = [...new Set([...cobroPolIds, ...renovPolIds])];
      if (polIdsEstado.length) {
        const { data: eps } = await supabase.from('polizas')
          .select('id, estado, numero, fecha_vencimiento, clientes(nombre)').in('id', polIdsEstado);
        (eps || []).forEach(p => { estadoPol[p.id] = p.estado; polInfo[p.id] = p; });
      }
      for (const tarea of cobroAbiertas) {
        if (estadoPol[tarea.poliza_id] === 'cancelada' || !polizasConDeuda.has(Number(tarea.poliza_id))) {
          idsCerrar.add(tarea.id);
        }
      }

      // 6.b Auto-cerrar tareas de RENOVACIÓN cuya póliza YA se renovó (o se canceló).
      //     Sin esto la tarea se queda abierta/'en proceso' para siempre: es el único tipo
      //     que no tiene cierre automático NI botón de cerrar a mano. `yaRenovada` se
      //     calculó arriba (sección 1), que es la MISMA verdad que decide si se crea.
      //     OJO: una póliza 'vencida' SIN renovar NO cierra su tarea — ahí sí queda algo que
      //     decidir (recuperar al cliente o darla por perdida), y esconderla sería perderla.
      for (const tarea of renovAbiertas) {
        const pol   = polInfo[tarea.poliza_id] || {};
        const est   = estadoPol[tarea.poliza_id];
        const polFV = pol.fecha_vencimiento ? String(pol.fecha_vencimiento).slice(0, 10) : null;

        // (a) La póliza quedó marcada renovada/cancelada, o ya existe la vigencia siguiente.
        let cerrar = est === 'renovada' || est === 'cancelada' || yaRenovada.has(Number(tarea.poliza_id));
        // (b) RENOVADA SOBRE EL MISMO REGISTRO: en vez de crear un registro para la vigencia
        //     nueva, se le movió la fecha (y a veces el número) al registro existente. No hay
        //     póliza que la apunte ni solicitud convertida, así que (a) no la ve nunca.
        //     La señal es que la póliza YA NO vence dentro de la ventana de renovación
        //     (60 días): no hay nada que renovar ahora. Igual cubre un endoso que extendió
        //     la vigencia. Se usa la ventana y no "cualquier fecha mayor" a propósito: una
        //     corrección de un día no debe cerrar la tarea y perder su historial; para eso
        //     basta con refrescarle el texto abajo.
        if (!cerrar && polFV && polFV > en60Str) cerrar = true;
        // (c) Duplicado de una decisión ya tomada: alguien cerró antes esta misma vigencia
        //     como "Cliente no renueva" y la auditoría vieja la recreó. Se cierra sola y el
        //     historial dice por qué (así se limpian las que ya se duplicaron).
        if (!cerrar && decidioNoRenovar(tarea.poliza_id, polFV)) {
          histDuplicadas.push({
            gestion_id: tarea.id, usuario_id: user?.id, accion: 'nota',
            detalle: 'Cerrada automáticamente: esta vigencia ya se había cerrado como "Cliente no renueva".',
          });
          cerrar = true;
        }
        if (cerrar) { idsCerrar.add(tarea.id); continue; }

        // La que sigue viva: refrescar su texto a la fecha de HOY (los cobros ya lo hacían).
        // Un texto congelado que dice "vence en 5 días" tres meses después hace que la
        // persona no distinga lo urgente de lo viejo.
        const t = textoRenovacion(pol.numero || tarea.poliza_id, pol.clientes?.nombre, polFV);
        if (t.titulo !== tarea.titulo || t.descripcion !== tarea.descripcion || t.prioridad !== tarea.prioridad) {
          updatesTexto.push(updateRow('crm_gestiones', tarea.id, {
            titulo: t.titulo, descripcion: t.descripcion, prioridad: t.prioridad,
          }));
          actualizadas++;
        }
      }

      // 7. Auto-cerrar tareas de endoso/cotización/reclamo/seguimiento cuyo origen ya se resolvió.
      //    Los estados de origen se piden en dos consultas (solicitudes / reclamos), no una por tarea.
      //    Las de 'seguimiento' con solicitud_id son las auto-generadas ("recibir póliza/documento
      //    de aseguradora"): cuando la solicitud se convierte en póliza su función terminó y deben
      //    cerrarse. Las de seguimiento SIN solicitud_id (manuales/recordatorios) NO se tocan aquí.
      const origenAbiertas = abiertas.filter(t =>
        ['endoso','cotizacion','reclamo'].includes(t.tipo) ||
        (t.tipo === 'seguimiento' && t.solicitud_id));
      const solIds7 = [...new Set(origenAbiertas.filter(t => t.solicitud_id).map(t => t.solicitud_id))];
      const recIds7 = [...new Set(origenAbiertas.filter(t => t.reclamo_id && !t.solicitud_id).map(t => t.reclamo_id))];
      const [solRes, recRes] = await Promise.all([
        solIds7.length ? supabase.from('solicitudes').select('id, estado').in('id', solIds7) : Promise.resolve({ data: [] }),
        recIds7.length ? supabase.from('reclamos').select('id, estado').in('id', recIds7) : Promise.resolve({ data: [] }),
      ]);
      const solEstado = {}, recEstado = {};
      (solRes.data || []).forEach(s => { solEstado[s.id] = s.estado; });
      (recRes.data || []).forEach(r => { recEstado[r.id] = r.estado; });
      for (const t of origenAbiertas) {
        let cerrar = false;
        if (t.solicitud_id) {
          // Para endoso/seguimiento, 'enviado_cliente' también cierra la tarea (fin del ciclo).
          const cerrarEstados = (t.tipo === 'endoso' || t.tipo === 'seguimiento')
            ? ['convertida','aceptada','rechazada','cancelada','enviado_cliente']
            : ['convertida','aceptada','rechazada','cancelada'];
          const est = solEstado[t.solicitud_id];
          if (est && cerrarEstados.includes(est)) cerrar = true;
        } else if (t.reclamo_id) {
          const est = recEstado[t.reclamo_id];
          if (est && ['aprobado','rechazado','cerrado'].includes(est)) cerrar = true;
        }
        if (cerrar) idsCerrar.add(t.id);
      }

      // ── Escrituras agrupadas: un insert en bloque, un UPDATE ... IN para cerrar ──
      if (nuevasTareas.length) {
        const { data: ins } = await supabase.from('crm_gestiones').insert(nuevasTareas).select('id');
        creadas += (ins || nuevasTareas).length;
      }
      if (updatesTexto.length) await Promise.all(updatesTexto);
      if (idsCerrar.size) {
        await supabase.from('crm_gestiones')
          .update({ estado: 'cerrado', actualizado_en: new Date().toISOString() })
          .in('id', [...idsCerrar]);
      }
      if (histDuplicadas.length) {
        try { await supabase.from('crm_historial').insert(histDuplicadas); } catch { /* la nota no debe frenar la auditoría */ }
      }

      // Sincronizar la asignación de TODAS las tareas abiertas (incluye las que ya
      // existían) al responsable correcto según la config actual.
      try { await sincronizarAsignaciones(); } catch (e) { console.error('sync asignaciones', e); }

      localStorage.setItem('crm_last_gen', String(Date.now()));
      setResumenGen({ creadas, actualizadas });
      await load();
    } catch (e) { alert('Error al generar tareas: ' + e.message); }
    finally { setGenerando(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">CRM de Gestión</div>
          <div className="page-subtitle">
            {rows.length} gestiones
            {vencidas > 0 && <span className="text-danger" style={{ marginLeft:8 }}>· {vencidas} vencidas</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* Las tareas se generan/refrescan/cierran solas al entrar al CRM (con freno de 10 min).
              El botón fuerza el refresco de inmediato; el indicador se muestra mientras corre. */}
          {generando ? (
            <span style={{ display:'flex', alignItems:'center', fontSize:12, color:'var(--silver)' }}>⚙️ Actualizando tareas…</span>
          ) : (
            <button className="btn btn-secondary" onClick={generarTareasAutomaticas} title="Refrescar las tareas automáticas ahora">
              <RefreshCw size={16} /> Actualizar tareas
            </button>
          )}
          {canEdit('crm') && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Nueva Gestión</button>}
        </div>
      </div>

      {resumenGen !== null && (() => {
        const hubo = (resumenGen.creadas || 0) + (resumenGen.actualizadas || 0) > 0;
        const partes = [];
        if (resumenGen.creadas > 0)      partes.push(`${resumenGen.creadas} tarea(s) nueva(s)`);
        if (resumenGen.actualizadas > 0) partes.push(`${resumenGen.actualizadas} actualizada(s)`);
        return (
        <div style={{ marginBottom:16, padding:'10px 16px', background: hubo ? 'rgba(34, 197, 94, 0.1)' : 'rgba(var(--sky-rgb),0.1)',
          borderRadius:8, border: `1px solid ${hubo ? 'rgba(34,197,94,0.3)' : 'rgba(46,125,196,0.3)'}`,
          fontSize:13, color: hubo ? '#22c55e' : 'var(--sky-light)',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span>{hubo ? `✅ ${partes.join(' · ')} (renovaciones y cobros vencidos)` : '✅ Todo al día — no se generaron tareas nuevas'}</span>
          <button onClick={()=>setResumenGen(null)} style={{ background:'none', border:'none', color:'inherit', cursor:'pointer', fontSize:16 }}>×</button>
        </div>
        );
      })()}

      <div className="toolbar">
        <PolizaSelect value={filterPoliza} onChange={setFilterPoliza} width={220} />
        <ClienteSelect value={filterCliente} onChange={setFilterCliente} placeholder="Filtrar por cliente..." width={220} />
        <select className="form-select" style={{ width:150 }} value={filterTipo} onChange={e=>setFilterTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-select" style={{ width:160 }} value={filterEstado} onChange={e=>setFilterEstado(e.target.value)}>
          <option value="dia">📅 Del día</option>
          <option value="">Todas (abiertas)</option>
          <option value="abierto">Abierto</option>
          <option value="en_proceso">En proceso</option>
          <option value="cerrado">Cerrado</option>
          <option value="pospuesta">⏸ Pospuestas</option>
        </select>
        <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:'1px solid rgba(var(--silver-rgb),0.2)' }}>
          {[['mis','Mis tareas'],['todos','Todos']].map(([v,l]) => (
            <button key={v} onClick={()=>setFilterAsignado(v)} style={{
              padding:'7px 14px', fontSize:12, border:'none', cursor:'pointer', fontFamily:'var(--font)',
              background: filterAsignado===v ? 'var(--sky)' : 'transparent',
              color: filterAsignado===v ? '#fff' : 'var(--silver)',
            }}>{l}</button>
          ))}
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr>
            {[['prioridad','Prioridad'],['tipo','Tipo'],['titulo','Título'],['cliente','Cliente'],
              ['poliza_ejecutivo','Ejecutivo'],['poliza_agentes','Agente'],
              ['asignado','Asignado a'],['vencimiento','Vencimiento'],['estado','Estado']].map(([col,label]) => (
              <th key={col} onClick={()=>handleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} title="Ordenar">
                {label}{sortIcon(col)}
              </th>
            ))}
            <th>Acciones</th>
          </tr></thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon"><ClipboardList size={48} /></div>
                <div className="empty-state-title">{filterEstado === 'dia' ? 'Nada que gestionar hoy 🎉' : 'Sin gestiones'}</div>
                {filterEstado === 'dia' && <div>Estás al día. Cambia a "Todas (abiertas)" para ver el resto o "⏸ Pospuestas" para las pospuestas.</div>}
              </div></td></tr>
            ) : sortedRows.map(r => {
              const vencida    = r.fecha_vencimiento && r.fecha_vencimiento < hoy && r.estado !== 'cerrado';
              const pospuesta  = r.posponer_hasta && r.posponer_hasta > hoy;
              return (
                <tr key={r.id} style={pospuesta ? { opacity:0.6 } : vencida ? { background:'rgba(239, 68, 68, 0.04)' } : {}}>
                  <td><span className={`badge ${PRIORIDAD_COLOR[r.prioridad]}`}>{r.prioridad}</span></td>
                  <td><span className="badge badge-neutral">{r.tipo}</span></td>
                  <td style={{ fontWeight:600, maxWidth:200 }}>
                    {r.titulo}
                    {pospuesta && <div style={{ fontSize:10, color:'var(--text-warning)', marginTop:2 }}>⏸ Hasta {r.posponer_hasta}</div>}
                  </td>
                  <td className="text-silver">{r.cliente_nombre || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{r.poliza_ejecutivo || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{r.poliza_agentes || '—'}</td>
                  <td>
                    {r.asignado_nombre ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                        <span style={{ fontSize:13 }}>{r.asignado_nombre}</span>
                        {r.asignador_nombre && <span style={{ fontSize:10, color:'var(--silver)' }}>por {r.asignador_nombre}</span>}
                      </div>
                    ) : <span className="text-silver">—</span>}
                  </td>
                  <td>
                    {r.fecha_vencimiento ? (
                      <span style={{ color: vencida ? 'var(--danger)' : 'inherit' }}>
                        {fmtFecha(r.fecha_vencimiento)}
                        {vencida && <AlertCircle size={12} style={{ marginLeft:4, display:'inline' }} />}
                      </span>
                    ) : '—'}
                  </td>
                  <td><span className={`badge ${r.estado==='cerrado'?'badge-neutral':r.estado==='abierto'?'badge-success':'badge-warning'}`}>{r.estado}</span></td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-sm btn-secondary" onClick={()=>openView(r)}>Gestionar</button>
                      {/* cobro, renovacion, placa y endoso-con-solicitud SOLO se cierran con acción concreta */}
                      {r.estado !== 'cerrado' && canEdit('crm') && !['cobro','renovacion'].includes(r.tipo) && !esTareaPlaca(r) && !esTareaEndosoBloqueada(r) && (
                        <button className="btn btn-sm btn-secondary" onClick={()=>cerrar(r)}>
                          <CheckCircle size={12} /> Cerrar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: Crear */}
      {modal === 'create' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Nueva Gestión</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Título *</label>
                  <input className="form-input" value={form.titulo} onChange={e=>f('titulo', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={form.tipo} onChange={e=>f('tipo', e.target.value)}>
                    {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={form.prioridad} onChange={e=>f('prioridad', e.target.value)}>
                    {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Cliente</label>
                  <ClienteSelect options={clientes} value={form.cliente_id}
                    onChange={val => f('cliente_id', val)} placeholder="Sin cliente — buscar..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Póliza</label>
                  <select className="form-select" value={form.poliza_id} onChange={e=>f('poliza_id', e.target.value)}>
                    <option value="">Sin póliza</option>
                    {polizas.map(p => <option key={p.id} value={p.id}>{p.numero} — {p.cliente}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Agente</label>
                  <select className="form-select" value={form.agente_id} onChange={e=>f('agente_id', e.target.value)}>
                    <option value="">Sin agente</option>
                    {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha límite</label>
                  <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e=>f('fecha_vencimiento', e.target.value)} />
                </div>

                {/* Asignar a otro usuario */}
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <UserCheck size={13} /> Asignar a
                  </label>
                  <select className="form-select" value={form.asignado_a} onChange={e=>f('asignado_a', e.target.value)}>
                    <option value="">Sin asignación (mía)</option>
                    {usuarios.filter(u=>u.id!==user?.id).map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                  </select>
                </div>
                {form.asignado_a && (
                  <div className="form-group" style={{ gridColumn:'span 2' }}>
                    <label className="form-label">Motivo de asignación</label>
                    <input className="form-input" value={form.motivo_asignacion} onChange={e=>f('motivo_asignacion', e.target.value)} placeholder="¿Por qué se asigna esta tarea?" />
                  </div>
                )}

                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Descripción</label>
                  <textarea className="form-textarea" value={form.descripcion} onChange={e=>f('descripcion', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave}>Crear Gestión</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ver */}
      {modal === 'view' && selected && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" style={{ maxWidth:780 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{selected.titulo}</div>
                <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                  <span className={`badge ${PRIORIDAD_COLOR[selected.prioridad]}`}>{selected.prioridad}</span>
                  <span className="badge badge-neutral">{selected.tipo}</span>
                  {selected.asignado_nombre && (
                    <span className="badge badge-info" style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <UserCheck size={10} /> {selected.asignado_nombre}
                    </span>
                  )}
                  {selected.posponer_hasta && selected.posponer_hasta > hoy && (
                    <span className="badge badge-warning" style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <Clock size={10} /> Pospuesta hasta {selected.posponer_hasta}
                    </span>
                  )}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>

            {/* Tabs dentro del modal */}
            <div style={{ display:'flex', borderBottom:'1px solid var(--steel)', padding:'0 24px' }}>
              {[
                ['info', 'Gestión'],
                ...(pagosPoliza.length > 0 ? [['pagos', `Plan de Cobros (${pagosPoliza.length})`]] : []),
                ...(endososTarea.length > 0 ? [['endosos', `Endosos (${endososTarea.length})`]] : []),
              ].map(([key, label]) => (
                <button key={key} onClick={()=>setTabVista(key)} style={{
                  padding:'10px 16px', background:'none', border:'none', cursor:'pointer',
                  fontFamily:'var(--font)', fontSize:13, fontWeight:600,
                  color: tabVista === key ? 'var(--sky)' : 'var(--silver)',
                  borderBottom: tabVista === key ? '2px solid var(--sky)' : '2px solid transparent',
                  marginBottom:-1,
                }}>{label}</button>
              ))}
            </div>

            <div className="modal-body" style={{ maxHeight:'70vh', overflowY:'auto' }}>

              {/* ── Tab: Gestión ── */}
              {tabVista === 'info' && (<>
                <div className="form-grid form-grid-3" style={{ marginBottom:16 }}>
                  {[
                    ['Cliente', selected.cliente_nombre],
                    ['Agente', selected.agente_nombre || polAgentesNombres],
                    ['Vencimiento tarea', fmtFecha(selected.fecha_vencimiento)],
                    ['Estado', selected.estado],
                    ['Creado', fmtFecha(selected.creado_en)],
                    // Datos de la póliza (solo si la tarea tiene póliza vinculada)
                    ...(polizaDetalle ? [
                      ['Aseguradora', polizaDetalle.aseguradoras?.nombre],
                      ['Vigencia', (polizaDetalle.fecha_inicio || polizaDetalle.fecha_vencimiento)
                        ? `${fmtFecha(polizaDetalle.fecha_inicio) || '—'} — ${fmtFecha(polizaDetalle.fecha_vencimiento) || '—'}` : null],
                      ['Prima total', polizaDetalle.prima != null
                        ? fmtMoneda(Number(polizaDetalle.prima) * (1 + (Number(polizaDetalle.impuesto) || 0) / 100)) : null],
                    ].filter(([,v]) => v != null && String(v).trim() !== '') : []),
                    // Contactos del cliente (solo los que existan) — en la misma grilla
                    ...(clienteContacto ? [
                      ['Teléfono',       clienteContacto.telefono],
                      ['Celular',        clienteContacto.celular || clienteContacto.whatsapp],
                      ['Email',          clienteContacto.email],
                      ['Contacto',       clienteContacto.contacto_nombre],
                      ['Tel. contacto',  clienteContacto.contacto_telefono],
                      ['Email contacto', clienteContacto.contacto_email],
                      ['Contacto cobro', clienteContacto.contacto_cobro_nombre],
                      ['Tel. cobro',     clienteContacto.contacto_cobro_telefono],
                      ['Email cobro',    clienteContacto.contacto_cobro_email],
                    ].filter(([,v]) => v != null && String(v).trim() !== '') : []),
                  ].map(([l,v]) => (
                    <div key={l}>
                      <div className="text-xs text-silver" style={{ marginBottom:2, textTransform:'uppercase' }}>{l}</div>
                      <div style={{ fontSize:14 }}>{v || '—'}</div>
                    </div>
                  ))}
                  {/* Póliza clickeable */}
                  {selected.poliza_num && (
                    <div>
                      <div className="text-xs text-silver" style={{ marginBottom:2, textTransform:'uppercase' }}>Póliza</div>
                      <button onClick={()=>setVerPolizaId(selected.poliza_id)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'var(--sky-light)', fontSize:14, fontWeight:700, padding:0, textDecoration:'underline', fontFamily:'Outfit,sans-serif' }}>
                        {selected.poliza_num} ↗
                      </button>
                    </div>
                  )}
                </div>

                {selected.asignado_nombre && (
                  <div className="card card-sm" style={{ marginBottom:16, borderLeft:'3px solid var(--sky)' }}>
                    <div style={{ fontSize:12, color:'var(--silver)', marginBottom:4 }}>ASIGNADO A</div>
                    <div style={{ fontSize:14, fontWeight:600 }}>{selected.asignado_nombre}</div>
                    {selected.asignador_nombre && <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>por {selected.asignador_nombre}</div>}
                    {selected.motivo_asignacion && <div style={{ fontSize:13, marginTop:6 }}>{selected.motivo_asignacion}</div>}
                  </div>
                )}

                {selected.descripcion && (
                  <div className="card card-sm" style={{ marginBottom:16 }}>
                    <div className="text-xs text-silver" style={{ marginBottom:4 }}>DESCRIPCIÓN</div>
                    <div style={{ fontSize:14, whiteSpace:'pre-wrap' }}>{selected.descripcion}</div>
                  </div>
                )}

                {/* Log de la tarea — gestiones realizadas (posponer / reasignar / correo). */}
                <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>LOG DE LA TAREA</div>
                <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:190, overflowY:'auto', marginBottom:20 }}>
                  {historial.length === 0 ? (
                    <div className="text-silver text-sm">Sin actividad registrada.</div>
                  ) : historial.map((h,i) => (
                    <div key={i} style={{
                      padding:'5px 10px', background:'rgba(var(--steel-rgb),0.25)', borderRadius:6,
                      borderLeft: h.accion === 'posponer' ? '3px solid #f59e0b'
                        : h.accion === 'asignacion' ? '3px solid var(--sky)' : '3px solid rgba(var(--silver-rgb),0.25)'
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'baseline' }}>
                        <span style={{ fontSize:13, lineHeight:1.3, flex:1 }}>{h.detalle}</span>
                        <span style={{ fontSize:10, color:'var(--silver)', whiteSpace:'nowrap' }}>{fmtFechaHora(h.creado_en)}</span>
                      </div>
                      <div style={{ fontSize:10, color:'var(--silver)' }}>{h.usuario || 'Sistema'} · {h.accion}</div>
                    </div>
                  ))}
                </div>

                {/* Bitácora de la póliza — notas YA registradas (el campo para escribir va después del ciclo) */}
                {selected.poliza_id && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:10 }}>
                      {selected.tipo === 'cobro' ? 'BITÁCORA DE COBRO' : 'BITÁCORA DE PÓLIZA'}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:180, overflowY:'auto' }}>
                      {bitacora.length === 0
                        ? <div className="text-silver text-sm">Sin notas todavía.</div>
                        : bitacora.map((b, i) => (
                          <div key={i} style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.4)', borderRadius:8,
                            borderLeft: selected.tipo === 'cobro' ? '3px solid rgba(var(--sky-rgb),0.6)' : '3px solid rgba(34, 197, 94, 0.5)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                              <span style={{ fontSize:11, color:'var(--silver)' }}>{b.usuario_nombre || 'Sistema'}{b.tipo ? ` · ${b.tipo}` : ''}</span>
                              <span style={{ fontSize:11, color:'var(--silver)' }}>{fmtFechaHora(b.creado_en)}</span>
                            </div>
                            <div style={{ fontSize:13 }}>{b.nota}</div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Acción especial: Renovación aceptada */}
                {selected.tipo === 'renovacion' && selected.estado !== 'cerrado' && selected.poliza_id && canEdit('crm') && (
                  <div style={{ padding:'12px 14px', background:'rgba(34, 197, 94, 0.08)', border:'1px solid rgba(34, 197, 94, 0.2)', borderRadius:8, marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--text-success)', marginBottom:6 }}>¿El cliente aceptó la renovación?</div>
                    <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                      Esto genera una solicitud de renovación pre-poblada con los datos de la póliza actual. La podrás editar en Solicitudes antes de convertirla.
                    </div>
                    <button className="btn btn-sm btn-success" onClick={generarSolicitudRenovacion} disabled={generandoRenov}>
                      <RefreshCw size={13}/> {generandoRenov ? 'Generando...' : '✅ Cliente acepta — Generar solicitud de renovación'}
                    </button>
                  </div>
                )}

                {/* ── Cierre contextual por tipo ── */}
                {selected.estado !== 'cerrado' && canEdit('crm') && (
                  <div style={{ marginBottom:16 }}>
                    {/* NOTIFICAR AL CLIENTE POR CORREO (cobro / renovación) */}
                    {['cobro','renovacion'].includes(selected.tipo) && (() => {
                      const plActiva = !!notifPlantillas[selected.tipo]?.activo;
                      const motivo = !notifCfg.habilitado ? 'Las notificaciones por correo aún no están activadas (Admin → Notificaciones).'
                        : !plActiva ? 'La plantilla de este tipo está inactiva (actívala en Admin → Notificaciones).'
                        : !clienteCorreo ? 'El cliente no tiene correo registrado.' : '';
                      const disabled = !!motivo;
                      return (
                        <div style={{ marginBottom:10, padding:'12px 14px', background:'rgba(var(--sky-rgb),0.07)', border:'1px solid rgba(var(--sky-rgb),0.22)', borderRadius:8 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                            <Mail size={13}/> Notificar al cliente por correo
                          </div>
                          <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                            Arma la plantilla con los datos reales de la póliza. Verás una vista previa editable; al enviar, la gestión queda registrada y podrás posponer la tarea.
                          </div>
                          <button className="btn btn-sm btn-primary" disabled={disabled || cargandoCorreo}
                            title={disabled ? motivo : ''} onClick={abrirPreviewCorreo}>
                            <Mail size={13}/> {cargandoCorreo ? 'Preparando...' : 'Enviar correo al cliente'}
                          </button>
                          {disabled && <div style={{ fontSize:11, color:'var(--text-warning)', marginTop:6 }}>{motivo}</div>}
                        </div>
                      );
                    })()}

                    {/* COBRO: solo cierra si póliza fue cancelada */}
                    {selected.tipo === 'cobro' && (
                      <div style={{ padding:'12px 14px', background:'rgba(239, 68, 68, 0.07)', border:'1px solid rgba(239, 68, 68, 0.25)', borderRadius:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-danger)', marginBottom:6 }}>¿Por qué se cierra esta gestión de cobro?</div>
                        <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                          Esta tarea se cierra <strong>automáticamente</strong> cuando se aplica el cobro pendiente. Solo ciérrala manualmente si la póliza fue cancelada.
                        </div>
                        {closingType === 'poliza_cancelada' ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            <textarea className="form-textarea" rows={2} value={closeNota} onChange={e=>setCloseNota(e.target.value)}
                              placeholder="Describe la cancelación (motivo, fecha efectiva, quién solicitó)..." style={{ fontSize:12 }}/>
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="btn btn-sm btn-danger" onClick={()=>handleCerrarConMotivo('Póliza cancelada', closeNota)}>
                                Confirmar cierre
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={()=>{ setClosingType(null); setCloseNota(''); }}>Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(239, 68, 68, 0.4)', color:'var(--text-danger)' }}
                            onClick={()=>setClosingType('poliza_cancelada')}>
                            🚫 Póliza cancelada — cerrar gestión
                          </button>
                        )}
                      </div>
                    )}

                    {/* RENOVACIÓN: cliente no renueva */}
                    {selected.tipo === 'renovacion' && (
                      <div style={{ padding:'12px 14px', background:'rgba(239, 68, 68, 0.07)', border:'1px solid rgba(239, 68, 68, 0.25)', borderRadius:8, marginTop:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-danger)', marginBottom:6 }}>¿El cliente no desea renovar?</div>
                        {closingType === 'no_renueva' ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            <textarea className="form-textarea" rows={2} value={closeNota} onChange={e=>setCloseNota(e.target.value)}
                              placeholder="Motivo de no renovación (cambio de aseguradora, cancelación, sin respuesta, etc.)..." style={{ fontSize:12 }}/>
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="btn btn-sm btn-danger" onClick={()=>handleCerrarConMotivo('Cliente no renueva', closeNota)}>
                                Confirmar cierre
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={()=>{ setClosingType(null); setCloseNota(''); }}>Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(239, 68, 68, 0.4)', color:'var(--text-danger)' }}
                            onClick={()=>setClosingType('no_renueva')}>
                            ❌ Cliente no renueva — cerrar gestión
                          </button>
                        )}
                      </div>
                    )}

                    {/* TAREA DE PLACA: cierre blindado (placa real + registro vehicular) */}
                    {!['cobro','renovacion'].includes(selected.tipo) && esTareaPlaca(selected) && (
                      <div style={{ padding:'12px 14px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', marginBottom:6 }}>🚗 Registrar placa y cerrar tarea</div>
                        <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                          Esta tarea <strong>solo</strong> se cierra registrando la placa real y adjuntando el registro vehicular (RUV).
                          La placa se guarda en la póliza y el RUV queda en los <strong>documentos de la póliza</strong>.
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                          <input className="form-input" value={placaNum}
                            onChange={e=>setPlacaNum(e.target.value)}
                            placeholder="Número de placa (ej: AB1234)"
                            style={{ textTransform:'uppercase' }} />
                          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer', margin:0 }}>
                              📎 Adjuntar registro vehicular (RUV)
                              <input type="file" accept="image/*,application/pdf" style={{ display:'none' }}
                                onChange={e=>setPlacaFile(e.target.files?.[0] || null)} />
                            </label>
                            {placaFile && <span style={{ fontSize:12, color:'var(--sky-light)' }}>{placaFile.name}</span>}
                          </div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button className="btn btn-sm btn-primary" onClick={resolverPlaca}
                              disabled={resolviendoPlaca || !placaNum.trim() || !placaFile}>
                              <CheckCircle size={13}/> {resolviendoPlaca ? 'Guardando...' : 'Guardar placa y cerrar tarea'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ENDOSO ligado a solicitud: ciclo obligatorio + cierre blindado */}
                    {esTareaEndosoBloqueada(selected) && (
                      <div style={{ padding:'12px 14px', background:'rgba(245, 158, 11, 0.07)', border:'1px solid rgba(245, 158, 11, 0.25)', borderRadius:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-warning)', marginBottom:8 }}>Ciclo del endoso — complétalo en orden</div>
                        {/* Pasos del ciclo con el actual marcado */}
                        <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
                          {ENDOSO_PASOS.map(paso => {
                            const idxActual = ENDOSO_ORDEN.indexOf(endosoSolEstado || 'borrador');
                            const idxPaso   = ENDOSO_ORDEN.indexOf(paso.key);
                            const hecho     = idxActual >= idxPaso;
                            return (
                              <div key={paso.key} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                                <span style={{ width:16, height:16, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center',
                                  background: hecho ? '#22c55e' : 'transparent', border: hecho ? 'none' : '1px solid var(--silver)', color:'var(--text)', fontSize:10, flexShrink:0 }}>
                                  {hecho ? '✓' : ''}
                                </span>
                                <span style={{ color: hecho ? '#e2e8f0' : 'var(--silver)', fontWeight: hecho ? 600 : 400 }}>{paso.label}</span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Acción del paso ACTUAL — avanza el ciclo (enlazado a la solicitud) */}
                        {(() => {
                          const est = endosoSolEstado || 'borrador';

                          // 1) Borrador → enviar a la aseguradora
                          if (est === 'borrador') {
                            return (
                              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                                <div style={{ fontSize:12, color:'var(--silver)' }}>Envía la solicitud a la aseguradora y márcala. Después usa <strong>Posponer tarea</strong> mientras esperas respuesta.</div>
                                <button className="btn btn-sm btn-primary" style={{ alignSelf:'flex-start' }} disabled={avanzandoEndoso}
                                  onClick={()=>avanzarCicloEndoso('enviada_aseg')}>
                                  → Marcar: Enviada a aseguradora
                                </button>
                              </div>
                            );
                          }

                          // 2) Enviada a aseguradora → recibir el endoso: adjuntar + validar (IA opcional) + registrar
                          if (est === 'enviada_aseg') {
                            const iaOk = iaResultado && iaResultado.coincide === true;
                            const puedeRegistrar = !!endosoDocUrl && (iaOk || confirmoEndoso);
                            const pasoLbl = { fontSize:11, fontWeight:700, color:'var(--sky-light)', marginBottom:6 };
                            return (
                              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                                <div style={{ fontSize:12, color:'var(--silver)' }}>Cuando la aseguradora te devuelva el endoso, complétalo en 3 pasos:</div>
                                {endosoMovioPrima && (
                                  <div style={{ fontSize:11, color:'var(--text-warning)', lineHeight:1.4, marginTop:-6 }}>Si el endoso recibido está <strong>incorrecto</strong>, <strong>regístralo igual</strong> (la aseguradora ya lo emitió). Como mueve prima, la tarea <strong>quedará abierta</strong> para dar seguimiento: la aseguradora enviará una corrección que se registra en esta misma tarea.</div>
                                )}

                                {/* Paso 1 — adjuntar el endoso */}
                                <div>
                                  <div style={pasoLbl}>1 · Adjunta el endoso recibido {endosoDocUrl && <span style={{ color:'var(--text-success)' }}>✓</span>}</div>
                                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                    <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer', margin:0 }}>
                                      📎 {endosoDocUrl ? 'Reemplazar' : 'Elegir archivo'}
                                      <input type="file" accept="image/*,application/pdf" style={{ display:'none' }}
                                        onChange={e=>setEndosoDocFile(e.target.files?.[0] || null)} />
                                    </label>
                                    {endosoDocFile && <span style={{ fontSize:12, color:'var(--sky-light)' }}>{endosoDocFile.name}</span>}
                                    {endosoDocFile && (
                                      <button className="btn btn-sm btn-primary" disabled={subiendoEndosoDoc} onClick={subirEndosoRecibido}>
                                        {subiendoEndosoDoc ? 'Subiendo...' : 'Adjuntar'}
                                      </button>
                                    )}
                                    {endosoDocUrl && !endosoDocFile && <span style={{ fontSize:12, color:'var(--text-success)' }}>endoso adjunto</span>}
                                  </div>
                                </div>

                                {/* Paso 2 — validar con IA (opcional) */}
                                <div style={{ opacity: endosoDocUrl ? 1 : 0.5 }}>
                                  <div style={pasoLbl}>2 · Valida con IA <span style={{ color:'var(--silver)', fontWeight:400 }}>(opcional)</span></div>
                                  <button className="btn btn-secondary btn-sm" disabled={!endosoDocUrl || analizandoIA} onClick={analizarEndosoIA}>
                                    🤖 {analizandoIA ? 'Analizando...' : 'Analizar con IA'}
                                  </button>
                                  {iaResultado && (
                                    <div style={{ fontSize:12, marginTop:8, padding:'10px 12px', borderRadius:8,
                                      background: iaResultado.error ? 'rgba(var(--silver-rgb),0.1)' : iaResultado.coincide ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                      border: `1px solid ${iaResultado.error ? 'rgba(143,168,192,0.3)' : iaResultado.coincide ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}` }}>
                                      {iaResultado.error
                                        ? <span style={{ color:'var(--silver)' }}>{iaResultado.error}</span>
                                        : <>
                                            <div style={{ fontWeight:700, color: iaResultado.coincide ? '#22c55e' : '#ef4444' }}>
                                              {iaResultado.coincide ? '✓ El endoso coincide con lo solicitado' : '✗ El endoso NO coincide con lo solicitado'}
                                            </div>
                                            {iaResultado.resumen && <div style={{ color:'#e2e8f0', marginTop:4 }}>{iaResultado.resumen}</div>}
                                            {Array.isArray(iaResultado.discrepancias) && iaResultado.discrepancias.length > 0 && (
                                              <ul style={{ margin:'6px 0 0', paddingLeft:18, color:'var(--text-danger)' }}>
                                                {iaResultado.discrepancias.map((d,i)=><li key={i}>{d}</li>)}
                                              </ul>
                                            )}
                                          </>}
                                    </div>
                                  )}
                                </div>

                                {/* Paso 3 — confirmar y registrar */}
                                <div style={{ opacity: endosoDocUrl ? 1 : 0.5 }}>
                                  <div style={pasoLbl}>3 · Confirma y regístralo en la póliza</div>
                                  {!iaOk && (
                                    <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#e2e8f0', cursor:'pointer', marginBottom:8 }}>
                                      <input type="checkbox" checked={confirmoEndoso} onChange={e=>setConfirmoEndoso(e.target.checked)} style={{ width:15, height:15, accentColor:'var(--sky)' }} />
                                      Confirmo que revisé el endoso recibido y procedo a registrarlo.
                                    </label>
                                  )}
                                  <button className="btn btn-sm btn-primary" style={{ alignSelf:'flex-start' }}
                                    disabled={!puedeRegistrar}
                                    title={!endosoDocUrl ? 'Primero adjunta el endoso recibido' : !puedeRegistrar ? 'Valida con IA o confirma que lo revisaste' : ''}
                                    onClick={irARegistrarEndoso}>
                                    → Registrar endoso en la póliza
                                  </button>
                                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>Se abre el registro (prima/cuotas); al terminar vuelves a esta tarea.</div>
                                </div>
                              </div>
                            );
                          }

                          // 3) Endoso recibido (ya registrado) → (opcional: corrección si mueve prima) + enviar al cliente + cerrar
                          if (est === 'endoso_recibido') {
                            return (
                              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                                <div style={{ fontSize:12, color:'#e2e8f0' }}>✓ El endoso <strong>ya quedó registrado en la póliza</strong>.</div>
                                {endosoMovioPrima ? (
                                  <div style={{ padding:'10px 12px', borderRadius:8, background:'rgba(245, 158, 11, 0.08)', border:'1px solid rgba(245, 158, 11, 0.25)' }}>
                                    <div style={{ fontSize:12, color:'var(--text-warning)', fontWeight:700, marginBottom:4 }}>Si el endoso vino con un error</div>
                                    <div style={{ fontSize:11.5, color:'var(--silver)', marginBottom:8, lineHeight:1.4 }}>
                                      Igual se factura en el sistema (la aseguradora ya lo emitió). La aseguradora debe enviar una <strong>corrección</strong>; mientras tanto <strong>NO cierres la tarea</strong>: se sigue dando seguimiento aquí. Cuando llegue la corrección, regístrala en esta misma tarea. Si el endoso está correcto, ignora esto y envíalo al cliente.
                                    </div>
                                    <button className="btn btn-secondary btn-sm" onClick={irARegistrarEndoso}>
                                      → Registrar corrección del endoso
                                    </button>
                                  </div>
                                ) : null}
                                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>Cuando el endoso esté correcto, <strong>envíaselo al cliente</strong> para cerrar la tarea:</div>
                                <button className="btn btn-sm btn-primary" style={{ alignSelf:'flex-start' }}
                                  disabled={avanzandoEndoso}
                                  onClick={()=>{ setEmailModalMode('file'); setEmailClienteFile(null); setEmailTexto(''); setEmailModal(true); }}>
                                  → Enviado al cliente (adjuntar correo y cerrar)
                                </button>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div style={{ fontSize:11, color:'var(--silver)', marginTop:10 }}>
                          La tarea se cierra al marcar <strong>"Enviado al cliente"</strong>, o si <strong>eliminas la solicitud</strong> con su motivo.
                        </div>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                          {selected.solicitud_id && (
                            <button className="btn btn-sm btn-secondary"
                              onClick={()=>{ sessionStorage.setItem('openSolicitudId', String(selected.solicitud_id)); sessionStorage.setItem('backFrom','crm'); navigate('/solicitudes'); }}>
                              Abrir la solicitud de endoso
                            </button>
                          )}
                          {/* Reversar ciclo — SOLO superusuario: elimina el endoso registrado y vuelve a "Enviada a aseguradora" */}
                          {user?.es_master && (endosoSolEstado === 'enviada_aseg' || endosoSolEstado === 'endoso_recibido') && (
                            <button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(239, 68, 68, 0.4)', color:'var(--text-danger)' }}
                              disabled={reversando} onClick={reversarCicloEndoso}
                              title="Superusuario: elimina el endoso registrado (sin dejar rastro), borra los documentos y regresa a Enviada a aseguradora">
                              <RefreshCw size={12}/> {reversando ? 'Reversando...' : 'Reversar ciclo (superusuario)'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* OTROS TIPOS: nota obligatoria */}
                    {!['cobro','renovacion'].includes(selected.tipo) && !esTareaPlaca(selected) && !esTareaEndosoBloqueada(selected) && (
                      <div style={{ padding:'12px 14px', background:'rgba(34, 197, 94, 0.06)', border:'1px solid rgba(34, 197, 94, 0.2)', borderRadius:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-success)', marginBottom:6 }}>Marcar como completada</div>
                        {closingType === 'completada' ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            <textarea className="form-textarea" rows={2} value={closeNota} onChange={e=>setCloseNota(e.target.value)}
                              placeholder="¿Qué acción concreta se realizó? (obligatorio)" style={{ fontSize:12 }}/>
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="btn btn-sm btn-primary" onClick={()=>handleCerrarConMotivo('Completada', closeNota)}>
                                <CheckCircle size={13}/> Confirmar cierre
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={()=>{ setClosingType(null); setCloseNota(''); }}>Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(34, 197, 94, 0.3)' }}
                            onClick={()=>setClosingType('completada')}>
                            <CheckCircle size={13}/> Completar gestión
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Gestión de la tarea: cada gestión es POSPONER o REASIGNAR, con su comentario.
                    La tarea NO se cierra a mano — se cierra sola al cumplirse su función
                    (cobro→se paga, renovación→se cancela/no renueva, endoso→enviado al cliente, etc.). */}
                {selected.estado !== 'cerrado' && canEdit('crm') && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>GESTIONAR TAREA</div>
                    <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                      Cada gestión queda en la bitácora con tu comentario y lo que hiciste (pospuesto hasta… o reasignado a…).
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {!showPosponer && (
                        <button className="btn btn-sm btn-secondary" onClick={()=>{ setShowPosponer(true); setShowAsignar(false); }}>
                          <Clock size={13}/> Comentar y posponer
                        </button>
                      )}
                      {!showAsignar && (
                        <button className="btn btn-sm btn-secondary" onClick={()=>{ setShowAsignar(true); setShowPosponer(false); }}>
                          <UserCheck size={13}/> Comentar y reasignar
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Panel posponer */}
                {showPosponer && (
                  <div style={{ background:'rgba(245, 158, 11, 0.08)', border:'1px solid rgba(245, 158, 11, 0.25)', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--text-warning)', marginBottom:10 }}>⏸ COMENTAR Y POSPONER</div>
                    <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                      Tu comentario queda en la bitácora junto con la fecha. La tarea desaparecerá de tu lista y volverá a aparecer ese día.
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      <input className="form-textarea" value={notaPosponer}
                        onChange={e=>setNotaPosponer(e.target.value)}
                        placeholder="Qué gestionaste hoy (obligatorio) — ej: llamé al cliente, paga el viernes; pendiente documento de la aseguradora..."
                        style={{ fontSize:12, minHeight:60 }} />
                      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <input className="form-input" type="date" value={fechaPosponer}
                          min={new Date(Date.now()+86400000).toISOString().slice(0,10)}
                          onChange={e=>setFechaPosponer(e.target.value)}
                          style={{ width:180 }} />
                        <button className="btn btn-sm" style={{ background:'#f59e0b', color:'#000', fontWeight:700 }}
                          onClick={handlePosponer} disabled={!fechaPosponer || !notaPosponer.trim()}>
                          Confirmar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={()=>{ setShowPosponer(false); setNotaPosponer(''); }}>Cancelar</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Panel asignar */}
                {showAsignar && (
                  <div style={{ background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', marginBottom:10 }}>👤 COMENTAR Y REASIGNAR</div>
                    <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>
                      La tarea pasa de inmediato al usuario que elijas — a partir de ahí él la gestiona (pospone / reasigna). Tu comentario queda en la bitácora.
                    </div>
                    <div className="form-grid form-grid-2">
                      <div className="form-group" style={{ margin:0 }}>
                        <label className="form-label">Reasignar a *</label>
                        <select className="form-select" value={asignandoA} onChange={e=>setAsignandoA(e.target.value)}>
                          <option value="">Seleccionar usuario...</option>
                          {usuarios.filter(u=>u.id!==user?.id).map(u=><option key={u.id} value={u.id}>{u.nombre}</option>)}
                        </select>
                      </div>
                      <div className="form-group" style={{ margin:0 }}>
                        <label className="form-label">Comentario *</label>
                        <input className="form-input" value={motivoAsig} onChange={e=>setMotivoAsig(e.target.value)} placeholder="Qué gestionaste y por qué lo reasignas (obligatorio)" />
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, marginTop:10, justifyContent:'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={()=>{ setShowAsignar(false); setAsignandoA(''); setMotivoAsig(''); }}>Cancelar</button>
                      <button className="btn btn-primary btn-sm" onClick={handleAsignar} disabled={!asignandoA || !motivoAsig.trim()}>Reasignar</button>
                    </div>
                  </div>
                )}

              </>)}

              {/* ── Tab: Plan de Cobros ── */}
              {tabVista === 'pagos' && (
                <div>
                  <div style={{ marginBottom:12, fontSize:13, color:'var(--silver)' }}>
                    Plan de cobros de la póliza <strong style={{ color:'var(--sky-light)' }}>{selected.poliza_num}</strong>
                  </div>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Cuota</th><th>Monto</th><th>Vencimiento</th><th>Estado</th><th>Fecha pago</th><th>Recibo {abrev}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagosPoliza.map((p,i) => {
                          const venc = p.fecha_vencimiento && p.fecha_vencimiento < hoy && p.estado === 'pendiente';
                          return (
                            <tr key={i} style={venc ? { background:'rgba(239, 68, 68, 0.06)' } : {}}>
                              <td style={{ color:'var(--silver)', fontSize:12 }}>{p.cuota_num ? `Cuota ${p.cuota_num}` : '—'}</td>
                              <td style={{ fontWeight:600, color:'var(--text-success)' }}>{fmt$(p.monto)}</td>
                              <td style={{ color: venc ? '#ef4444' : 'inherit' }}>
                                {fmtFecha(p.fecha_vencimiento) || '—'}
                                {venc && <span style={{ marginLeft:6, fontSize:10, color:'var(--text-danger)', fontWeight:700 }}>VENCIDA</span>}
                              </td>
                              <td>
                                <span className={`badge ${p.estado==='pagado'?'badge-success':venc?'badge-danger':'badge-warning'}`}>{p.estado}</span>
                              </td>
                              <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(p.fecha_pago) || '—'}</td>
                              <td style={{ fontSize:11, color:'var(--sky-light)', fontFamily:'monospace' }}>{p.num_recibo_ru || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Resumen */}
                  {(() => {
                    const total   = pagosPoliza.reduce((s,p) => s+(parseFloat(p.monto)||0), 0);
                    const pagado  = pagosPoliza.filter(p=>p.estado==='pagado').reduce((s,p) => s+(parseFloat(p.monto)||0), 0);
                    const pendiente = total - pagado;
                    const vencidas = pagosPoliza.filter(p=>p.estado==='pendiente'&&p.fecha_vencimiento&&p.fecha_vencimiento<hoy).length;
                    return (
                      <div style={{ display:'flex', gap:20, marginTop:14, padding:'10px 14px', background:'rgba(var(--navy-rgb),0.4)', borderRadius:8, fontSize:13 }}>
                        <span style={{ color:'var(--silver)' }}>Total: <strong style={{ color:'var(--text)' }}>{fmt$(total)}</strong></span>
                        <span style={{ color:'var(--silver)' }}>Pagado: <strong style={{ color:'var(--text-success)' }}>{fmt$(pagado)}</strong></span>
                        <span style={{ color:'var(--silver)' }}>Pendiente: <strong style={{ color: pendiente>0?'#f59e0b':'#22c55e' }}>{fmt$(pendiente)}</strong></span>
                        {vencidas > 0 && <span style={{ color:'var(--text-danger)', fontWeight:700 }}>⚠️ {vencidas} cuota(s) vencida(s)</span>}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── Tab: Endosos de la póliza ── */}
              {tabVista === 'endosos' && (
                <div>
                  <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
                    Endosos registrados en la póliza <strong style={{ color:'var(--sky-light)' }}>{selected.poliza_num}</strong> — útil cuando el cobro viene de un endoso.
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {endososTarea.map((e,i)=>{
                      const delta = parseFloat(e.delta_prima)||0;
                      const anulado = e.estado === 'anulado';
                      return (
                        <div key={i} style={{ padding:'10px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.15)', opacity:anulado?0.55:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            <span style={{ flex:1, minWidth:150, fontSize:13, fontWeight:600, textDecoration:anulado?'line-through':'none' }}>
                              {e.descripcion || 'Endoso'}
                              {e.tipo === 'cancelacion' && <span className="badge badge-danger" style={{ marginLeft:6 }}>Cancelación</span>}
                              {e.tipo === 'prorroga' && <span className="badge badge-success" style={{ marginLeft:6 }}>Prórroga</span>}
                              {e.numero_endoso && <span style={{ color:'var(--silver)', fontWeight:400, marginLeft:8, fontSize:11 }}>N° {e.numero_endoso}</span>}
                            </span>
                            <span style={{ fontSize:11, color:'var(--silver)', whiteSpace:'nowrap' }}>{fmtFecha(e.fecha)||fmtFecha(e.creado_en)||'—'}</span>
                            <span style={{ fontSize:13, fontWeight:700, color: delta>0?'#22c55e':delta<0?'#ef4444':'var(--silver)', whiteSpace:'nowrap' }} title="Cambio en prima">{delta>0?'+':''}{fmt$(delta)}</span>
                            <span className={`badge ${anulado?'badge-danger':'badge-success'}`} style={{ whiteSpace:'nowrap' }}>{anulado?'anulado':(e.estado||'activo')}</span>
                            {e.url_documento && (
                              <a href="#" onClick={(ev)=>{ ev.preventDefault(); abrirDocumento(e.url_documento); }} title="Documento del endoso" style={{ color:'var(--sky-light)', display:'inline-flex', alignItems:'center', textDecoration:'none' }}>📎</a>
                            )}
                          </div>
                          {anulado && e.motivo_anulacion && (
                            <div style={{ fontSize:11, color:'var(--text-danger)', marginTop:4 }}>Motivo de anulación: <span style={{ color:'var(--text-danger)' }}>{e.motivo_anulacion}</span></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ══ Vista de la póliza (la MISMA del módulo Pólizas), como capa sobre la tarea.
             Al cerrarla se vuelve al modal de la tarea, que sigue abierto por debajo. ══ */}
      {verPolizaId && (
        <PolizaVistaModal polizaId={verPolizaId} onClose={()=>setVerPolizaId(null)} />
      )}

      {/* ══ Modal: Vista previa de correo al cliente ═══════════════ */}
      {correoPreview && (
        <div className="modal-overlay" style={{ zIndex:1200 }} onClick={()=>!enviandoCorreo && setCorreoPreview(null)}>
          <div className="modal" style={{ maxWidth:560 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <Mail size={16}/> Correo al cliente — {correoPreview.tipo === 'cobro' ? 'Cobro' : 'Renovación'}
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setCorreoPreview(null)} disabled={enviandoCorreo}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom:10 }}>
                <label className="form-label">Para</label>
                <input className="form-input" value={correoPreview.destinatario} disabled />
              </div>
              <div className="form-group" style={{ marginBottom:10 }}>
                <label className="form-label">Asunto</label>
                <input className="form-input" value={correoPreview.asunto}
                  onChange={e=>setCorreoPreview(p=>({ ...p, asunto:e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Mensaje</label>
                <textarea className="form-textarea" rows={9} value={correoPreview.cuerpo}
                  onChange={e=>setCorreoPreview(p=>({ ...p, cuerpo:e.target.value }))} style={{ resize:'vertical' }} />
              </div>
              {!notifCfg.habilitado && (
                <div style={{ fontSize:11, color:'var(--text-warning)', marginTop:8 }}>
                  El envío real aún no está activo: la gestión se registrará igual y el correo saldrá cuando se configure el correo en Cloudflare.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setCorreoPreview(null)} disabled={enviandoCorreo}>Cancelar</button>
              <button className="btn btn-primary" onClick={enviarCorreo}
                disabled={enviandoCorreo || !correoPreview.asunto.trim() || !correoPreview.cuerpo.trim()}>
                <Mail size={14}/> {enviandoCorreo ? 'Enviando...' : 'Enviar y posponer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Enviar al cliente (adjuntar PDF/imagen o pegar el texto del correo) ══ */}
      {emailModal && (
        <div className="modal-overlay" style={{ zIndex:1200 }} onClick={()=>!enviandoCliente && setEmailModal(false)}>
          <div className="modal" style={{ maxWidth:560 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Enviar el endoso al cliente</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setEmailModal(false)} disabled={enviandoCliente}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
                Deja el respaldo de que le enviaste el endoso al cliente: <strong>adjunta el correo</strong> (PDF o imagen) <strong>o pega el texto</strong> del correo que enviaste.
              </div>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                {[['file','📎 Adjuntar PDF / imagen'],['texto','✍️ Pegar texto del correo']].map(([m,l])=>(
                  <button key={m} className={`btn btn-sm ${emailModalMode===m?'btn-primary':'btn-secondary'}`}
                    onClick={()=>setEmailModalMode(m)}>{l}</button>
                ))}
              </div>
              {emailModalMode === 'file' ? (
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer', margin:0 }}>
                    📎 {emailClienteFile ? 'Cambiar archivo' : 'Elegir archivo'}
                    <input type="file" accept="image/*,application/pdf,.eml,.msg" style={{ display:'none' }}
                      onChange={e=>setEmailClienteFile(e.target.files?.[0] || null)} />
                  </label>
                  {emailClienteFile && <span style={{ fontSize:12, color:'var(--sky-light)' }}>{emailClienteFile.name}</span>}
                </div>
              ) : (
                <textarea className="form-textarea" rows={8} value={emailTexto} onChange={e=>setEmailTexto(e.target.value)}
                  placeholder="Pega aquí el texto del correo que le enviaste al cliente..." style={{ fontSize:13, resize:'vertical', width:'100%' }} />
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setEmailModal(false)} disabled={enviandoCliente}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarEnvioCliente}
                disabled={enviandoCliente || (emailModalMode==='texto' ? !emailTexto.trim() : !emailClienteFile)}>
                {enviandoCliente ? 'Guardando...' : 'Guardar y cerrar la tarea'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
